import { mileageBandLabel } from '../../../utils/mileageBands';
import { useProConversations } from '../../../hooks/useProConversations';
import { useCommunicationNavigation } from '../../../contexts/CommunicationNavigationContext';
import { capacityDate, isRequestForPro, isOpen as isCapacityRequestOpen, useCapacityPreview } from '../../scheduling/capacity/model';
import { ProSchedulingRequests } from '../../scheduling/capacity/CapacityBoard';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { Ban, AlertCircle } from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import ordersService, { type OrderDetails, type OrderLeg } from '../../../services/orders.service';
import {
  Row,
  Stack,
  Label,
  LabelStacked,
  TextInput,
  SelectInput,
  FlagInput,
  Section,
  TabbedSection,
  Icon,
  type FlagValue,
} from './orderform';
import { v1WeightCascade } from '../../../utils/v1WeightConversion';
import { isWeightMissing } from '../../../utils/importWeightRule';
import BulkFieldCheckbox from '../changeAll/BulkFieldCheckbox';
import SingleSelect from '../../../components/SingleSelect';
import { useChangeAll } from '../changeAll/ChangeAllContext';
import { FieldHistoryProvider } from '../../../components/fieldmenu/FieldHistoryProvider';
import TroubleTicketsPanel from './TroubleTicketsPanel';
import RepairOrdersPanel from './RepairOrdersPanel';
import repairOrdersService from '../../../services/repairOrders.service';
import StorageClearedPopover from './StorageClearedPopover';
import GroundedDatesPopover from './GroundedDatesPopover';
import troubleTicketsService from '../../../services/troubleTickets.service';
import schedulingService from '../../../services/scheduling.service';
import ReschedulesPanel from './ReschedulesPanel';
import { useOrderFormContext } from '../../../contexts/OrderFormContext';
import InfoModal from '../../../components/common/InfoModal';
import WarningConfirmModal from '../../../components/ui/WarningConfirmModal';
import ContainerBadge from '../../../components/ContainerBadge';
import EquipmentLookup, { type EquipmentLookupItem } from '../../../components/EquipmentLookup';
import CarrierLookupField from '../../../components/CarrierLookupField';
import EntityEditLink from '../../../components/EntityEditLink';
// Lazy-loaded: the bespoke carrier create/edit drawer pulls in the RMIS lookup +
// carrier-contacts subtrees, which shouldn't weigh down the base pro-detail
// bundle — it only mounts when a user adds a new carrier inline.
const CarrierProfileEditDrawer = lazy(
  () => import('../../../components/tablemaint/CarrierProfileEditDrawer'),
);
import { CARRIER_DISPATCHERS } from '../../../utils/outsideCarrier';
import ChassisLocationButton from '../../../components/ChassisLocationButton';
import { getChassisTrackingStatus } from '../../../utils/chassisTracking';
import ScacInfoModal from '../../../components/pro/ScacInfoModal';
import ChassisSpecsModal from './ChassisSpecsModal';
import Notification from '../../../components/Notification';
import PermissionsContainer from '../../../components/PermissionsContainer';
import { useIsDisabledByPermission } from '../../../contexts/PermissionAccessContext';
import { useTryPermissions } from '../../../contexts/PermissionContext';
import { useOrderFieldDisplayContext } from '../OrderFieldDisplayContext';
import {
  isTemplatePronumber,
  isTemplateApproved,
  templateFieldEditable,
} from '../templateFieldPolicy';
import { useCanEditHazmat } from '../useCanEditHazmat';
import { useCanEditOrderLcFields } from '../useCanEditOrderLcFields';
import {
  useCanEditRailbillField,
  RAILBILL_FIELD_COMPONENT_ID,
} from '../useCanEditRailbillField';
import OrderLcFieldGroup from './OrderLcFieldGroup';
import PortLookupField from './PortLookupField';
import { CHASSIS_TYPES, EQUIPMENT_SIZES, EQUIPMENT_TYPES } from '../../../constants/orderFieldOptions';
import DateTimePicker from '../../../components/DateTimePicker';
import { parseMdYHm, formatMdYHm, parseMdYDate, formatMdYDate } from '../../../utils/dateUtils';
import { proDetailPath } from '../../../utils/proSearch';
import { useContainerValidation } from '../../../hooks/useContainerValidation';
import { validateChassisCompatibility, chassisErrorsContainField } from '../../../utils/chassisValidation';
import { useChassisCompatibility } from '../../../hooks/useChassisCompatibility';
import { useOverweightOrchestrator } from '../../../hooks/useOverweightOrchestrator';
import { getRails, type RailOption } from '../../../services/rails.service';
import { getScacs, type ScacOption } from '../../../services/scacs.service';
import { getCstDispatchers, type CstDispatcherOption } from '../../../services/cstDispatchers.service';

/**
 * Subset of the `useOrderEditing` hook surface that {@link OrderFormView}
 * actually uses. Accepting the focused subset (rather than the entire hook
 * return) makes the form testable and tolerant of the parent passing only
 * the bits it owns.
 */
export interface OrderFormEditing {
  editedOrderFields: Record<string, any>;
  /** True when the order has any unsaved edits (fields, legs, added/removed
   * legs). */
  hasChanges?: boolean;
  /** Like `hasChanges` but ignores legs whose only staged edit is the
   * `sendToGwiMove` dispatch flag (auto-staged by the Default-Send-to-GWI-Move
   * preference). Used to gate the Reschedule Requested trouble ticket so that
   * flag doesn't trigger a false "unsaved changes" warning. */
  hasChangesIgnoringDispatchFlags?: boolean;
  updateOrderField: (name: string, value: any) => void;
  /** Drop a staged order-field edit so the field snaps back to the value it had
   *  before this session's edits. Used by the Bkg Hold cascade to restore the
   *  three export dates when the order comes back off a POOL/ROLLING hold. */
  revertOrderField?: (name: string) => void;
  /**
   * Optional map of order-level conflicts surfaced by a parallel save (added
   * by the parent agent that owns the hook). When present, the matching
   * input gets the amber "stale" highlight matching the legacy drawer.
   */
  conflicts?: Record<string, { theirs: any; mine: any }>;
  /**
   * Effective legs (saved overlay + editedLegs + pending new legs - removed
   * legs). When present, the overweight driver re-qual walks these so unsaved
   * driver assignments are included. Falls back to `details.legs` if absent.
   */
  effectiveLegs?: ReadonlyArray<import('../../../services/orders.service').OrderLeg>;
  /**
   * Setter that injects save-blocking errors into the hook's `validateChanges`.
   * Used by the overweight driver re-qual flow to hard-block save when a
   * non-started leg has an assigned driver who no longer qualifies. See
   * useOrderEditing's `setExtraSaveErrors` for timing semantics.
   */
  setExtraSaveErrors?: (source: string, errors: string[]) => void;
  /**
   * The hook's FULL save-blocking validator — every rule, not just the
   * `validateOrderSave` subset this file owns (container-required, Terminal/
   * Mode, the create-only rules and the Delivery-Order gate all live in there).
   * The banner below calls it once `validationAttempted` is set so the list
   * re-evaluates on every edit.
   */
  validateChanges?: () => string[];
  /** True once a save/create was blocked by validation. */
  validationAttempted?: boolean;
}

/**
 * Props for {@link OrderFormView}.
 *
 * The form was read-only in earlier Pro 360 phases. As of this phase the 8
 * order-level fields wired below (dispatcher, chassisProvider, chassisType,
 * chassisId, sealNumber, dropReleaseDatetime, dropReleaseComment, comments)
 * are editable when the parent passes the `editing` prop. Fields without a
 * wiring stay read-only (no edit-mode toggle).
 */
export interface OrderFormViewProps {
  details: Partial<OrderDetails> | null;
  editing?: OrderFormEditing;
  /** Optional override for the Trouble Tickets sub-tab badge. */
  troubleTicketCount?: number;
  /** Bumped by the parent after an auto-triggered REJECTED trouble ticket is
   * saved, so the sub-tab badge count re-fetches (the TroubleTicketsPanel that
   * normally keeps it in sync is only mounted on the Trouble Tickets sub-tab). */
  troubleTicketRefreshNonce?: number;
  /**
   * Re-fetch the order details (legs, order columns). Invoked after a
   * trouble-ticket save triggers the Reschedule Requested side effect, so
   * cleared driver/appt dates + bumped reschedule count appear immediately.
   */
  onProRefresh?: () => void | Promise<void>;
  /** CSR/LC template-edit gate. On a template, fields only open when this is
   *  true (and the field policy allows it). Defaults to editable; ignored for
   *  a normal order. */
  canEditTemplate?: boolean;
  /** Create New Order mode — only changes the validation banner's wording
   *  ("Cannot create…" vs "Cannot save…"). */
  createMode?: boolean;
  /** Optional ➡️ run-trace control rendered next to the Terminal field (the v1
   *  click-trace on the order's rail/terminal). Passed from the host so this
   *  component needn't own the network call / toast. */
  terminalTraceControl?: React.ReactNode;
  /** Optional ➡️ run-trace control rendered next to the SCAC field (traces the
   *  SSLine side). Same pattern as terminalTraceControl. */
  scacTraceControl?: React.ReactNode;
  /** Fresh order values from the latest trace, overlaid *under* the user's edits
   *  (edited > traced > fetched) so a trace fills fields in place without a
   *  re-fetch. Keyed by OrderDetails key. */
  tracedOverlay?: Record<string, unknown>;
  /** OrderDetails keys the latest trace changed — highlighted on-screen. */
  tracedKeys?: string[];
  /** Rolling Fault set to a non-blank value while Booking Hold is ROLLING —
   *  open the "Reason for Rolling" prompt (V1 defs.pl:561 dofunc('rolling')).
   *  Owned by ProDetailContainer, which captures the values and saves the
   *  record. Absent (create mode / no host) = the fault is a plain field edit. */
  onRollingFaultSet?: (fault: string) => void;
}

const LABEL_W = 106;
const LABEL_W_MID = 64;
const LABEL_W_RIGHT = 92;
const LABEL_W_STATUS = 104;

const PLACEHOLDER = '—';

const YN: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];
const YNP: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
  { value: 'P', label: 'P' },
];


/** Tailwind classes that mirror the legacy drawer's amber conflict highlight. */
const CONFLICT_CLASS = 'bg-amber-50 dark:bg-amber-900/30 border-amber-500';

/** The three export dates a POOL/ROLLING Bkg Hold blanks (and restores on the way
 *  out) — see handleRollingChange. */
const ROLLING_HELD_DATES = ['firstRcv', 'cutoffDatetime', 'documentCutoffDatetime'] as const;

/**
 * V1 parity hard-block validations for order save. Pure function so the hook
 * can call it from `validateChanges` and the form can render the same list
 * inline. Returns an array of human-readable error strings; an empty array
 * means there are no blocking issues.
 *
 * V1 source-of-truth: `lib/lib_editrecord.pl` lines 1000-1700.
 *
 * Each rule uses "effective" values — edited value wins, then current.
 * Fields that don't exist on `OrderDetails` are flagged with TODO comments
 * and skipped silently so the validator never throws on missing data.
 */
export const CLASS9_ONLY_HAZMAT_MESSAGE =
  'Hazmat cannot be Y when the only hazmat lines are class 9. Class 9 is hazardous on water only.';

/**
 * True when the pro has at least one hazmat line AND every line is class 9.
 * Such a pro may not have Haz manually set to Y (class 9 is hazardous on water
 * only). Mirrors backend `app.utils.hazmat.is_class9_only`.
 */
export function isClass9Only(
  rows: ReadonlyArray<{ hazmatClass?: string }> | null | undefined,
): boolean {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.every((r) => String(r?.hazmatClass ?? '').trim() === '9');
}

export function validateOrderSave(
  edited: Record<string, any>,
  current: Partial<OrderDetails>,
  opts?: { isTemplate?: boolean; canEditHazmat?: boolean; hazmatLegActive?: boolean },
): string[] {
  const errors: string[] = [];
  const isTemplate = !!opts?.isTemplate;
  // Legacy parity (lib_editrecord.pl ~1469): the hazmat save-block only fired
  // for users with write ACL on the hazmat field. Default to read-only (skip
  // the block) to match our PermissionsContainers, which default to read-only;
  // callers pass the real editability (see useCanEditHazmat).
  const canEditHazmat = opts?.canEditHazmat ?? false;

  // Effective value: edited wins, then current.
  const eff = (key: string): unknown =>
    edited[key] !== undefined ? edited[key] : (current as Record<string, any>)[key];

  // Treat null/undefined/empty-string (after trim) as unfilled.
  const filled = (v: unknown): boolean =>
    v != null && String(v).trim() !== '';

  // A template is a blueprint — NONE of the live-order save validations below
  // apply (verified against the legacy template save gate): brokerage cargo trio,
  // rolling fault, reefer temps, inbond, hazmat, CCC vessel/voyage, tare range,
  // R4 (STD/HC — container is locked on templates), R8 (never marked
  // available-to-ship), and container-required are all order-only. Chassis/size +
  // ISO-TANK compatibility still applies to templates and runs in validateChanges.
  if (isTemplate) return errors;

  // Rule 1 (V1 lib_editrecord.pl 1262-1268) — brokerage requires cargo trio
  if (eff('dispatcher') === 'brokerage') {
    if (
      !filled(eff('commodity')) ||
      !filled(eff('pieceCount')) ||
      !filled(eff('lbs'))
    ) {
      errors.push(
        'Commodity, piece count, and weight are required for brokerage moves',
      );
    }
  }

  // Rule 2 (V1 1586-1590) — rolling fault required when Booking Hold (rolling) is
  // ROLLING or BOOKING DISCREPANCY. Confirmed against legacy 2026-07-21 (Lee): the
  // requirement keys off the rolling VALUE, not just movetype. Movetype gate kept
  // (rolling only shows on EXPORT / RAIL TO PORT via the display config anyway).
  const moveType = String(eff('moveType') ?? '');
  if (
    (moveType === 'EXPORT' || moveType === 'RAIL TO PORT') &&
    (eff('rolling') === 'ROLLING' || eff('rolling') === 'BOOKING DISCREPANCY') &&
    !filled(eff('rollingFault'))
  ) {
    errors.push(
      'Rolling Fault is required when Booking Hold is ROLLING or BOOKING DISCREPANCY',
    );
  }

  // Rule 3 (V1 lib_editrecord temperature checks; confirmed vs the legacy
  // front-end 2026-07-22): (a) a REEFER must declare whether it's
  // temperature-controlled; (b) a temperature-controlled order requires the full
  // genset + temp + scale + vent set.
  const equipTypeR3 = String(eff('equipType') ?? '');
  if (/REEFER/i.test(equipTypeR3) && !filled(eff('temperatureControlled'))) {
    errors.push('Please specify if temperature controlled.');
  }
  if (
    eff('temperatureControlled') === 'Y' &&
    (!filled(eff('temperature')) ||
      !filled(eff('genset')) ||
      !filled(eff('tempScale')) ||
      !filled(eff('ventSetting')))
  ) {
    errors.push(
      'Genset, temperature, scale, and vent setting are required when temperature-controlled',
    );
  }

  // Rule 4 (V1 1028) — STD/HC cannot have a container number
  if (filled(eff('container')) && eff('equipType') === 'STD/HC') {
    errors.push('STD/HC equipment cannot have a container number');
  }

  // Rule 5 (V1 1332-1340) — inbond type requires inbond number + responsible.
  if (filled(eff('inbondType'))) {
    if (!filled(eff('inbondNumber'))) {
      errors.push('Inbond Number is required when Inbond Type is set');
    }
    if (!filled(eff('inbondResponsible'))) {
      errors.push('Inbond Responsible is required when Inbond Type is set');
    }
  }

  // Rule 6 (V1 lib_editrecord.pl 1376-1392) — a hazmat=Y order must carry hazmat
  // info once the LOADED leg is active. "Info present" is class + UN (our
  // stand-in for "a haz line exists", since our hazmat table is shaped
  // differently); the pair can live on the order scalars OR on any row of the
  // hazmatInfo sub-table (Addl Info tab). "Loaded leg active" (opts.hazmatLegActive,
  // computed by the caller via hazmatInfoLegActive because it needs leg data):
  //   - EXPORT: a driver is on the terminate leg, or on the delivery leg when
  //     there is no terminate leg (so it pertains only to the loaded leg);
  //   - otherwise: the originate leg has a pickup appt (can't pick up a loaded
  //     hazardous container without knowing what's in it).
  // Gated on hazmat write access (V1 ACL) — read-only users can't fix it.
  if (canEditHazmat && eff('hazmat') === 'Y' && opts?.hazmatLegActive) {
    const scalarsFilled =
      filled(eff('hazmatClass')) && filled(eff('hazmatUn'));
    const infoRows = (eff('hazmatInfo') as unknown) as
      | ReadonlyArray<{ hazmatClass?: string; hazmatUn?: string }>
      | undefined;
    const anyInfoRowFilled = Array.isArray(infoRows)
      && infoRows.some((r) => filled(r?.hazmatClass) && filled(r?.hazmatUn));
    if (!scalarsFilled && !anyInfoRowFilled) {
      errors.push('Hazmat Info is missing.');
    }
  }

  // Class 9 is hazardous on water only: Haz may not be set to Y when the pro's
  // only hazmat lines are class 9. The form toggle blocks this at flip time;
  // this catches any other path that lands a Y on the edited fields. Server
  // mirror: orders.py _reject_hazmat_y_if_class9_only.
  if (edited['hazmat'] === 'Y' && isClass9Only(eff('hazmatInfo') as ReadonlyArray<{ hazmatClass?: string }> | undefined)) {
    errors.push(CLASS9_ONLY_HAZMAT_MESSAGE);
  }

  // Rule 7 (V1 1427-1431) — CCC terminal IMPORT/EXPORT requires vessel/voyage
  if (
    (moveType === 'IMPORT' || moveType === 'EXPORT') &&
    eff('terminal') === 'CCC' &&
    (!filled(eff('vessel')) || !filled(eff('voyage')))
  ) {
    errors.push('Vessel and Voyage are required for CCC terminal');
  }

  // Rule 8 (V1 1251-1260) — Available-to-Ship requires cargo trio.
  // Note: OrderDetails exposes `availToShipUser` (no "able").
  if (
    filled(eff('availToShipUser')) &&
    (!filled(eff('commodity')) ||
      !filled(eff('pieceCount')) ||
      !filled(eff('lbs')))
  ) {
    errors.push(
      'Commodity, piece count, and weight are required to mark Available to Ship',
    );
  }

  // Rule 9 (V1 1326-1328) — genset reefer requires temp trio
  const equipTypeR9 = String(eff('equipType') ?? '');
  if (
    eff('genset') === 'Y' &&
    /REEFER/i.test(equipTypeR9) &&
    (!filled(eff('temperature')) ||
      !filled(eff('tempScale')) ||
      !filled(eff('ventSetting')))
  ) {
    errors.push(
      'Temperature, scale, and vent setting are required for genset reefer',
    );
  }

  // Rule 10 (V1 checktarelbs, INC-6282) — tare weight 0/empty or 4000-15000.
  const tareLbsVal = eff('tareLbs');
  if (filled(tareLbsVal)) {
    const n = Number(tareLbsVal);
    if (n !== 0 && (Number.isNaN(n) || n < 4000 || n > 15000)) {
      errors.push('Tare weight must be between 4000 and 15000 lbs');
    }
  }

  // Rule 11 (V1 lib_editrecord.pl 1289) — Chassis Type is required unless the
  // equipment is one of the trailer / van / railcar types that never ride a
  // chassis. A BLANK equip type is NOT an escape: it falls outside the exemption
  // list, so it needs a chassis type like any other container move.
  //
  // This used to be gated on a *set* equip type, on the theory that a
  // barely-started order shouldn't be nagged. It doesn't work: the server runs
  // the same rule ungated on both the create and the save path
  // (chassis_size_rules.check_chassis_and_size, rule 2), so a blank-equip-type
  // order was blocked anyway — just as a 422 toast after the round trip instead
  // of an inline banner item alongside the size / provider errors. The create
  // flow seeds equipType blank (buildCreateOrderSeed's blank seed), so EVERY
  // no-template create hit exactly that hole. Matching the server means the user
  // sees one list of everything standing between them and a save.
  const equipTypeR11 = String(eff('equipType') ?? '').trim().toUpperCase();
  if (
    !CHASSIS_EXEMPT_EQUIP.has(equipTypeR11) &&
    !filled(eff('chassisType'))
  ) {
    errors.push('Chassis Type is required');
  }

  // Rule 12 (V1 1249) — a Target Delivery Date requires an ON/BY selection.
  if (filled(eff('targetDeliveryDate')) && !filled(eff('targetOnOrBy'))) {
    errors.push('You must select ON or BY for Target Delivery Date');
  }

  // TODO (V1 parity, not yet exposed in OrderDetails):
  //   - Flatbed dimensions (V1 1261: lengthInches, widthInches, heightInches,
  //     ooglegal). None of those are on OrderDetails today.

  return errors;
}

/**
 * V1 lib_editrecord.pl 1376-1392 — whether the LOADED leg is active enough to
 * require hazmat info on save. Split out from {@link validateOrderSave} because
 * it needs leg data (driver assignment / pickup appointment), which the pure
 * order-field validator doesn't take. Callers pass the result as
 * `opts.hazmatLegActive`.
 *
 * - EXPORT: a driver is on the terminate leg (or, when there is no terminate
 *   leg, on the delivery leg) — so the requirement pertains only to the loaded
 *   leg being dispatched.
 * - Otherwise: the originate leg has a pickup appointment — we can't pick up
 *   the loaded, hazardous container without knowing what's in it.
 */
export function hazmatInfoLegActive(
  current: Partial<OrderDetails>,
  legs: ReadonlyArray<OrderLeg>,
): boolean {
  const legBy = (n?: number): OrderLeg | undefined =>
    n ? legs.find((l) => l.legnum === n) : undefined;
  const hasDriver = (l?: OrderLeg): boolean =>
    !!l && String(l.driver ?? '').trim() !== '';
  if (current.moveType === 'EXPORT') {
    return current.terminateLeg
      ? hasDriver(legBy(current.terminateLeg))
      : hasDriver(legBy(current.deliveryLeg));
  }
  const orig = legBy(current.originateLeg);
  return !!orig && String(orig.origApptDate ?? '').trim() !== '';
}

// V1 lib_editrecord.pl 1289 — equipment types that never require a chassis
// (they carry no ocean container). Every other equip type — blank included —
// requires a Chassis Type on save.
const CHASSIS_EXEMPT_EQUIP: ReadonlySet<string> = new Set([
  'DOUBLE DROP TRAILER',
  'SINGLE DROP TRAILER',
  'FLATBED',
  'FLATBED WITH TARP',
  'VAN',
  'PNEUMATIC VAN',
  'REEFER VAN',
  'RAILCAR',
  'HOPPER CAR',
  'HOPPER BOTTOM',
]);

/** Coerce a possibly-undefined string to a renderable value. */
const s = (v: unknown): string => (v == null ? '' : String(v));

/**
 * Red alert icon that reveals an auto-pregate failure message. The native
 * title tooltip it replaces had the browser's ~1s hover delay; this shows
 * instantly on hover AND toggles open/closed on click (click again to close).
 * The popover is portaled to the nearest <dialog> (the ProDetail drawer lives
 * in the top layer) so it isn't painted behind the drawer.
 */
const PregateErrorIndicator: React.FC<{ message: string; label: string }> = ({ message, label }) => {
  const [pinned, setPinned] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const open = pinned || hovering;

  const computePos = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const pw = popRef.current?.offsetWidth ?? 288;
    const ph = popRef.current?.offsetHeight ?? 40;
    const pad = 8;
    let top = r.bottom + 6;
    let left = r.left;
    if (left + pw > window.innerWidth - pad) left = window.innerWidth - pw - pad;
    if (left < pad) left = pad;
    // Flip above the icon if it would overflow the bottom edge.
    if (top + ph > window.innerHeight - pad) top = r.top - ph - 6;
    if (top < pad) top = pad;
    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    computePos();
    window.addEventListener('scroll', computePos, true);
    window.addEventListener('resize', computePos);
    return () => {
      window.removeEventListener('scroll', computePos, true);
      window.removeEventListener('resize', computePos);
    };
  }, [open, message, computePos]);

  // Click-outside / Escape close the pinned popover.
  useEffect(() => {
    if (!pinned) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !popRef.current?.contains(t)) {
        setPinned(false);
        setHovering(false);
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setPinned(false); setHovering(false); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pinned]);

  const target =
    (btnRef.current?.closest('dialog') as HTMLElement | null) || document.body;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setPinned((prev) => {
          const next = !prev;
          // Turning off while the pointer is still over the icon: clear the
          // hover flag too so it closes now instead of staying up via hover.
          if (!next) setHovering(false);
          return next;
        })}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className="p-0.5 shrink-0 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 cursor-pointer transition-colors"
        aria-label={label}
        aria-expanded={open}
      >
        <AlertCircle className="w-4 h-4" />
      </button>
      {open && createPortal(
        <div
          ref={popRef}
          role="tooltip"
          className="fixed z-[9999] max-w-sm rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-xs leading-snug text-white shadow-lg whitespace-normal break-words dark:bg-gray-800 dark:text-gray-100"
          style={{ top: pos.top, left: pos.left }}
        >
          {message}
        </div>,
        target
      )}
    </>
  );
};

/** Coerce flag-shaped values to FlagInput's enum, defaulting to 'N'. */
const flag = (v: unknown): FlagValue => {
  if (v === 'Y' || v === 'N' || v === 'P') return v;
  return 'N';
};

/** Yellow-highlight a move flag when it's set to 'Y' (legacy CMS parity). */
const flagHl = (v: unknown): 'yellow' | undefined => (flag(v) === 'Y' ? 'yellow' : undefined);

/**
 * Export-hazmat preadvise state (feature/#33245). True when an EXPORT move has
 * Haz='Y' and a real preadvise datetime is recorded — the trigger for the green
 * Haz flag + "Preadvised" tag on the order screen. `startsWith('0000-00-00')`
 * filters the V1 zero-date sentinel (same guard as dropReleaseCheck).
 */
export function isExportHazmatPreadvised(args: {
  moveType?: string;
  hazmat?: unknown;
  preAdviseDatetime?: string | null;
}): boolean {
  const preadvise = String(args.preAdviseDatetime ?? '').trim();
  return (
    args.moveType === 'EXPORT' &&
    flag(args.hazmat) === 'Y' &&
    preadvise !== '' &&
    !preadvise.startsWith('0000-00-00')
  );
}

/**
 * Read-only display cell — renders a value as plain text aligned with the
 * row layout used for editable inputs. Non-editable fields use this so the
 * Order Info tab visually distinguishes editable from informational data.
 */
/** Exact red/orange/yellow cell colors from the LC Dashboard Exports board
 * (ExportsBoardPanel COLOR_CLASS), reused for cutoff-proximity highlighting so
 * the two surfaces stay visually identical. */
const EXPORTS_PROXIMITY_CLASS: Record<'red' | 'orange' | 'yellow', string> = {
  red: 'bg-red-600 text-white rounded',
  orange: 'bg-orange-400 text-black rounded',
  yellow: 'bg-yellow-300 text-black rounded',
};

/** Proximity tint spanning a whole Row (label + date + Y/N), matching the Exports
 *  board colors — used on the Cutoff / Doc Cutoff rows so the highlight is
 *  visible across the field, not just the value cell. */
const proximityRowCls = (tone: 'red' | 'orange' | 'yellow' | undefined): string =>
  tone ? `${EXPORTS_PROXIMITY_CLASS[tone]} px-1` : '';
/** Force the Label text to stay legible on the tinted row (white on red, black
 *  on the lighter orange/yellow); the Label otherwise hard-codes its own color. */
const proximityLabelCls = (tone: 'red' | 'orange' | 'yellow' | undefined): string | undefined =>
  tone === 'red' ? '!text-white' : tone ? '!text-black' : undefined;

interface ReadValueProps {
  value?: unknown;
  mono?: boolean;
  width?: number;
  ariaLabel?: string;
  tone?: 'red' | 'orange';
  /** Cutoff-proximity highlight using the Exports board colors. Takes
   *  precedence over `tone`; see {@link cutoffProximityTone}. */
  proximity?: 'red' | 'orange' | 'yellow';
}
const ReadValue: React.FC<ReadValueProps> = ({ value, mono, width, ariaLabel, tone, proximity }) => {
  const text = value == null || value === '' ? PLACEHOLDER : String(value);
  const isEmpty = text === PLACEHOLDER;
  const toneClass =
    tone === 'red' ? 'bg-brand-red text-white rounded'
      : tone === 'orange' ? 'bg-brand-orange text-white rounded'
        : '';
  const colorClass =
    (proximity ? EXPORTS_PROXIMITY_CLASS[proximity] : '')
    || toneClass
    || (isEmpty ? 'text-slate-400 dark:text-gray-500' : 'text-slate-900 dark:text-gray-100');
  const cls = [
    'inline-flex items-center h-6 px-1.5 text-[0.75rem] truncate',
    '',
    colorClass,
    width ? '' : 'flex-1 min-w-0',
  ]
    .filter(Boolean)
    .join(' ');
  const style = width ? { width: `${width}px` } : undefined;
  return (
    <span className={cls} style={style} aria-label={ariaLabel} title={isEmpty ? undefined : text}>
      {text}
    </span>
  );
};

/**
 * Parse the backend-formatted LFD string ("MM/dd" for current year or
 * "MM/dd/yy" otherwise) and return a tone keyed to proximity:
 * red when today or past due, orange when exactly one day out.
 */
function lfdProximityTone(value: unknown): 'red' | 'orange' | undefined {
  if (value == null || value === '') return undefined;
  const text = String(value).trim();
  const m = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/.exec(text);
  if (!m) return undefined;
  const month = parseInt(m[1], 10);
  const day = parseInt(m[2], 10);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let year: number;
  if (m[3]) {
    const yy = parseInt(m[3], 10);
    year = yy < 100 ? 2000 + yy : yy;
  } else {
    year = today.getFullYear();
  }
  const lfd = new Date(year, month - 1, day);
  lfd.setHours(0, 0, 0, 0);
  const diffDays = Math.round((lfd.getTime() - today.getTime()) / 86_400_000);
  if (diffDays <= 0) return 'red';
  if (diffDays === 1) return 'orange';
  return undefined;
}

/**
 * V1 `fv_lastfreedaydate` gate (formatview.pl ~2208): the LFD proximity color
 * only draws the eye while the port free-time clock is still actionable. Keyed
 * off the rail terminal's dotrace flag (V1 `railterminal.dotrace`, surfaced by
 * the backend as `railTraceable`):
 *   'Y' — traceable rail: flagged until the container is OUT-GATED.
 *   'N' — non-traceable rail row: flagged until the origin leg has a driver.
 *   '' / undefined — no rails row: never flagged.
 * Once the relevant milestone is hit, V1 drops the red so attention moves on;
 * 360 previously kept coloring purely on date proximity.
 */
export function isLfdColorActive(
  railTraceable: string | undefined,
  outGated: boolean,
  originHasDriver: boolean,
): boolean {
  if (railTraceable === 'Y') return !outGated;
  if (railTraceable === 'N') return !originHasDriver;
  return false;
}

/**
 * Chassis-specs trigger-icon alert color: draws the eye (amber) when the row
 * carries an OOG/broker-out/permit condition that hasn't been legalized yet.
 * `ooglegal === 'Y'` means the size condition is already squared away (legal
 * OOG), so it suppresses the alert regardless of the other flags. Otherwise,
 * any one of brokerout / oversizePermitRequired / overweightPermitRequired /
 * oog being 'Y' trips it.
 */
export function isChassisSpecsAlert(v: {
  ooglegal: string;
  brokerout: string;
  oversizePermitRequired: string;
  overweightPermitRequired: string;
  oog: string;
}): boolean {
  return (
    v.ooglegal !== 'Y' &&
    (v.brokerout === 'Y' ||
      v.oversizePermitRequired === 'Y' ||
      v.overweightPermitRequired === 'Y' ||
      v.oog === 'Y')
  );
}

/**
 * Cutoff-proximity tone, mirroring the LC Dashboard Exports board's
 * `_exp_date_color`: red when the cutoff date is today or earlier, orange when
 * tomorrow, yellow when two days out, otherwise none. Compared on the DATE part
 * only (time ignored, as the board does). Parses the backend cutoff format
 * "MM/dd[/yy] HH:mm" — current-year values omit the year (so the year is
 * inferred as today's), matching the order-details endpoint's DATE_FORMAT.
 */
function cutoffProximityTone(value: unknown): 'red' | 'orange' | 'yellow' | undefined {
  if (value == null || value === '') return undefined;
  const text = String(value).trim();
  // No trailing anchor: any " HH:mm" suffix is intentionally ignored.
  const m = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/.exec(text);
  if (!m) return undefined;
  const month = parseInt(m[1], 10);
  const day = parseInt(m[2], 10);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let year: number;
  if (m[3]) {
    const yy = parseInt(m[3], 10);
    year = yy < 100 ? 2000 + yy : yy;
  } else {
    year = today.getFullYear();
  }
  const cutoff = new Date(year, month - 1, day);
  cutoff.setHours(0, 0, 0, 0);
  const diffDays = Math.round((cutoff.getTime() - today.getTime()) / 86_400_000);
  if (diffDays <= 0) return 'red';
  if (diffDays === 1) return 'orange';
  if (diffDays === 2) return 'yellow';
  return undefined;
}

/**
 * True when a backend-formatted Per Diem date ("MM/dd" for the current year or
 * "MM/dd/yy" otherwise) is strictly before today — i.e. past due. Same parse
 * shape as {@link lfdProximityTone}; used to flag overdue per diem in red.
 */
function isPerDiemPastDue(value: unknown): boolean {
  if (value == null || value === '') return false;
  const text = String(value).trim();
  const m = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/.exec(text);
  if (!m) return false;
  const month = parseInt(m[1], 10);
  const day = parseInt(m[2], 10);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let year: number;
  if (m[3]) {
    const yy = parseInt(m[3], 10);
    year = yy < 100 ? 2000 + yy : yy;
  } else {
    year = today.getFullYear();
  }
  const perDiem = new Date(year, month - 1, day);
  perDiem.setHours(0, 0, 0, 0);
  return perDiem.getTime() < today.getTime();
}

/**
 * Renders the three weight inputs (lbs / kgs / MT) as either editable
 * TextInputs or read-only ReadValues. The parent computes the base
 * editability via the shared `canEdit` seam (permission-gated for orders,
 * policy-gated for templates) and passes the resolved boolean as `editable`.
 * The weight group additionally sits inside its own `order-weight-fields`
 * PermissionsContainer, so we AND in `useIsDisabledByPermission()` (read from
 * that container's provider) to preserve the admin-controlled weight lock.
 */
export type WeightField = 'lbs' | 'kgs' | 'metricTons';
export type WeightPatch = Record<WeightField, string>;

/** V1 conversion constants (orderupdate.pl's order cascade + the legacy
 *  kgconvert/mtconvert front-end pair). Nexa (customer 4525) carries a
 *  higher-precision lb→kg factor. */
/** The arithmetic itself is V1's, in one shared module — see the note there on
 *  why lbs->kgs multiplies by 0.4536 rather than dividing by 2.2046, and why
 *  every computed value carries ONE decimal. */
/** cms.ordersall.lbs/kgs/metrictons are all decimal(10,2) — derive to the same
 *  precision the columns actually hold. */
const WEIGHT_DECIMALS = 2;

/**
 * Weight conversion for the Lbs / Kgs / MT trio: given the field the user typed
 * in and its raw text, return the values for all three.
 *
 * The arithmetic is V1's `kgconvert`/`mtconvert`, via `v1WeightCascade` — each
 * target computed directly from the SOURCE unit with that pair's own factor,
 * rounded to one decimal. This used to derive everything through lbs and divide
 * by 2.2046, which drifted: 45000 lbs saved as 20411.87 kgs where V1 writes
 * 20412.00 (#33560, found by diffing test orders 5109864 / 5109879).
 *
 * The edited field keeps the RAW typed text — reformatting it mid-keystroke
 * would fight the user (typing "10" → "10.00" → the next digit lands as
 * "10.005"). It's normalized on blur instead.
 *
 * Returns null when the entry isn't a number yet (a lone '-', '1e', …) so the
 * caller can stage the keystroke without cascading garbage.
 */
export function weightCascade(
  source: WeightField,
  raw: string,
  opts: { isNexa?: boolean } = {},
): WeightPatch | null {
  // Users type big weights with separators ("6,863.45"); Number() would NaN.
  const trimmed = raw.trim().replace(/,/g, '');
  // An empty box zeroes the other two, but is itself left EMPTY: stamping '0.00'
  // back into the field the user just cleared put the caret after it, so the
  // next digits landed as "0.007000". Blur normalizes an empty box to '0.00'.
  if (trimmed === '') {
    return { lbs: '0.00', kgs: '0.00', metricTons: '0.00', [source]: '' };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  const w = v1WeightCascade(
    source === 'metricTons' ? 'metrictons' : source,
    n,
    { isNexa: opts.isNexa },
  );
  return {
    lbs: w.lbs,
    kgs: w.kgs,
    metricTons: w.metrictons,
    // The typed field keeps what was typed (see above).
    [source]: raw,
  };
}

/** Blur value for a weight field: an empty box settles to '0.00', anything
 *  numeric settles to the 2 decimals the column stores. Non-numeric text is
 *  left alone so the user can still see (and fix) what they typed. */
export function normalizeWeightEntry(raw: string): string | null {
  const trimmed = raw.trim().replace(/,/g, '');
  if (trimmed === '') return (0).toFixed(WEIGHT_DECIMALS);
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  const fixed = n.toFixed(WEIGHT_DECIMALS);
  return fixed === raw ? null : fixed;
}

interface WeightFieldsBodyProps {
  editable: boolean;
  fieldValue: (key: keyof OrderDetails) => unknown;
  applyWeightCascade: (source: WeightField, raw: string) => void;
  normalizeWeightField: (source: WeightField, raw: string) => void;
  conflictClass: (key: keyof OrderDetails) => string;
  overweight: boolean;
  d: Partial<OrderDetails>;
}
const WeightFieldsBody: React.FC<WeightFieldsBodyProps> = ({
  editable,
  fieldValue,
  applyWeightCascade,
  normalizeWeightField,
  conflictClass,
  overweight,
  d,
}) => {
  const disabledByPermission = useIsDisabledByPermission();
  const showEditable = editable && !disabledByPermission;
  // Lbs is authoritative; this warning applies to every move type without
  // changing the import-only missing-weight save rule.
  const missingWeight = isWeightMissing(showEditable ? fieldValue('lbs') : d.lbs);
  const weightHighlight = overweight || missingWeight ? 'red' : undefined;
  if (!showEditable) {
    return (
      <>
        <ReadValue value={d.lbs} mono width={64} ariaLabel="Pounds" tone={missingWeight ? 'red' : undefined} />
        <ReadValue value={d.kgs} mono width={56} ariaLabel="Kilograms" tone={missingWeight ? 'red' : undefined} />
        <ReadValue value={d.metricTons} mono width={56} ariaLabel="Metric tons" tone={missingWeight ? 'red' : undefined} />
      </>
    );
  }
  return (
    <>
      <BulkFieldCheckbox fieldName="lbs" />
      <TextInput
        value={s(fieldValue('lbs'))}
        placeholder={PLACEHOLDER}
        mono
        width={96}
        onChange={(v) => applyWeightCascade('lbs', v)}
        onBlur={(v) => normalizeWeightField('lbs', v)}
        ariaLabel="Pounds"
        highlight={weightHighlight}
        className={conflictClass('lbs')}
      />
      <BulkFieldCheckbox fieldName="kgs" />
      <TextInput
        value={s(fieldValue('kgs'))}
        placeholder={PLACEHOLDER}
        mono
        width={80}
        onChange={(v) => applyWeightCascade('kgs', v)}
        onBlur={(v) => normalizeWeightField('kgs', v)}
        ariaLabel="Kilograms"
        highlight={weightHighlight}
        className={conflictClass('kgs')}
      />
      <BulkFieldCheckbox fieldName="metricTons" />
      <TextInput
        value={s(fieldValue('metricTons'))}
        placeholder={PLACEHOLDER}
        mono
        width={72}
        onChange={(v) => applyWeightCascade('metricTons', v)}
        onBlur={(v) => normalizeWeightField('metricTons', v)}
        ariaLabel="Metric tons"
        highlight={weightHighlight}
        className={conflictClass('metricTons')}
      />
    </>
  );
};

/**
 * Visual rewrite of the legacy Order Info tab body — a dense 3-column grid
 * (People & Equipment, Move Flags & Schedule, Locations & Routing) plus a
 * bottom TabbedSection that toggles between Comments & Status and the
 * Trouble Tickets placeholder.
 *
 * Mirrors the prototype `OrderForm` (Pro360 single-file lines 1737-1990).
 * Editing wiring for 8 order-level fields is live (dispatcher,
 * chassisProvider, chassisType, chassisId, sealNumber, dropReleaseDatetime,
 * dropReleaseComment, comments). Other fields remain read-only.
 */
const OrderFormView: React.FC<OrderFormViewProps> = ({
  details,
  editing,
  troubleTicketCount,
  troubleTicketRefreshNonce,
  onProRefresh,
  canEditTemplate = true,
  createMode = false,
  terminalTraceControl,
  scacTraceControl,
  tracedOverlay,
  tracedKeys,
  onRollingFaultSet,
}) => {
  // Overlay the latest trace's fresh values UNDER the user's edits (edited >
  // traced > fetched) so a trace fills the on-screen fields in place, no
  // re-fetch. Merged into `d` here → fieldValue + every d.xxx read picks it up;
  // `isTraceHl` drives the yellow "updated by this trace" highlight.
  const d = { ...(details ?? {}), ...(tracedOverlay ?? {}) } as Partial<OrderDetails>;
  const traceHlKeys = new Set(tracedKeys ?? []);
  const isTraceHl = (key: string): boolean => traceHlKeys.has(key);
  const isExport = d.moveType === 'EXPORT' || d.moveType === 'RAIL TO PORT';
  const isRailToPort = d.moveType === 'RAIL TO PORT';
  // `bol` column label varies by movetype (legacy parity): "Booking" for the
  // export lanes (EXPORT / RAIL TO PORT), "BOL" for IMPORT, "Ref #" otherwise.
  const bolLabel = isExport ? 'Booking' : d.moveType === 'IMPORT' ? 'BOL' : 'Ref #';
  const editedOrderFields = editing?.editedOrderFields ?? {};
  const conflicts = editing?.conflicts ?? {};
  const updateOrderField = editing?.updateOrderField;

  // "Add As New Carrier" (V1 New Carrier Profile) — opened inline from the
  // Carrier typeahead's create-new action; seeds the name from the typed term
  // and, on create, auto-links the new carrier onto the order.
  const [newCarrierOpen, setNewCarrierOpen] = useState(false);
  const [newCarrierName, setNewCarrierName] = useState('');

  // Per-field editability seam. A template ("T"-prefixed pronumber) gates each
  // field via the template policy (approval-aware); a normal order keeps its
  // existing permission-gated behavior. For a real order `editing` is always
  // wired and there's no permission wrapper, so `canEdit` reduces to
  // `!!editing && !disabledByPermission` — byte-for-byte identical to before.
  const isTemplate = isTemplatePronumber(d.pronumber);
  const capacityPreview = useCapacityPreview();
  const communicationFeed = useProConversations(String(d.pronumber ?? ''));
  const conversations = communicationFeed.rows;
  const openCommunicationCount = conversations.filter(r => r.pro === String(d.pronumber) && r.status === 'OPEN').length;
  const openCapacityCount = capacityPreview.requests.filter(r => isCapacityRequestOpen(r) && isRequestForPro(r, String(d.pronumber))).length;
  const isApproved = isTemplateApproved(d);
  const disabledByPermission = useIsDisabledByPermission();
  // Gates the Rule 6 hazmat save-block. Resolves the order-lc-fields access
  // level (the grouping the Haz flag now lives in), so the "Hazmat class + UN
  // required" block is enforced for exactly the LC users who can edit the flag —
  // read-only users aren't blocked on unrelated saves (legacy write-ACL parity).
  const canEditHazmat = useCanEditHazmat();
  const canEdit = (fieldKey: string): boolean =>
    isTemplate
      ? canEditTemplate && !!editing && templateFieldEditable(fieldKey, { isApproved })
      : !!editing && !disabledByPermission;

  // LC-only order fields (Ports here). Same template branch, but the ORDER
  // branch is gated by the order-lc-fields permission (defaultDisabled) instead
  // of the component-level `!disabledByPermission`, so non-LC users can't edit
  // them on a real order — see useCanEditOrderLcFields + OrderLcFieldGroup.
  const canEditOrderLcFields = useCanEditOrderLcFields();
  // For the Available-to-Ship stamp (below) and the chassis-specs OOG-approval
  // stamps (oversize/overweight "Approve without Permit"): the CMS approver
  // name, falling back to the email, then to MSAL — mirrors AddlInfoView's
  // hazApprover pattern — so the stamp is NEVER an empty string. A user can
  // have an OOG-approval permission grant (gated on perms.groups/user_id) even
  // when their AD-synced v1_username/user_email has a data-quality gap; without
  // this tail that combination would stamp an approver-blank timestamp on a
  // compliance-sensitive control.
  const perms = useTryPermissions();
  const { accounts } = useMsal();
  const currentUsername = accounts[0]?.username || accounts[0]?.name || 'system';
  const currentUser =
    perms?.permissions?.v1_username || perms?.permissions?.user_email || currentUsername;
  // Config-driven movetype visibility (order_field_display_rules). `hidden(key)`
  // is the source of truth for whether a field shows on this movetype; the
  // isExport block stays as LAYOUT (labels/widths/groupings). Some keys are the
  // legacy DB column (e.g. 'portoforiginname') where that's what the seed
  // governs. Safe default (no provider) = nothing hidden = today's behavior.
  const disp = useOrderFieldDisplayContext();
  const hidden = (key: string): boolean => disp.isFieldHidden(key);
  // Railbill Y/N is dispatcher-editable via its own component permission
  // (railbill-flag), separate from the order-lc-fields grouping.
  const canEditRailbillField = useCanEditRailbillField();
  // `editable = user-may-write AND shown AND not-readonly` — the contract
  // order_field_display_rules declares. The readonly half had no consumer until
  // #33501, so the seeded readonly rules were inert on the form; crossing it in here
  // is what makes them real, and what lets Admin Override release the ones that opted
  // in (the hook drops those from the readonly set while override is on).
  // Templates keep their own policy as the sole authority.
  const canEditLc = (fieldKey: string): boolean =>
    isTemplate
      ? canEditTemplate && !!editing && templateFieldEditable(fieldKey, { isApproved })
      : !!editing && canEditOrderLcFields && !disp.isFieldReadonly(fieldKey);

  // Storage Cleared applies to imports / rail-to-port (container sitting at the
  // port). Always offer the control there — "Cleared thru …" when a date exists,
  // "Clear Storage" otherwise — so storage can be cleared regardless of whether
  // an LFD is set. The popover lists the history and appends a new cleared date.
  // Storage-cleared is a live/ops feature (append-only storagecleared table),
  // not relevant to templates — and its numeric pronumber would be NaN there.
  const showStorageCleared =
    !isTemplate && (d.moveType === 'IMPORT' || d.moveType === 'RAIL TO PORT');
  const [scPopoverOpen, setScPopoverOpen] = useState(false);
  const [scButtonRect, setScButtonRect] = useState<DOMRect | null>(null);

  const {
    allDispatchers,
    csrs,
    chassisProviders,
    chassisEquipment,
    vanEquipment,
    setEffectiveDispatcherInputs,
  } = useOrderFormContext();

  // Push the current dispatcher state into OrderFormContext so the provider's
  // derived effectiveDispatcher / effectiveDispatcherIsGwi stay in sync with
  // this surface. Downstream consumers (driver-gating in Phase 1, legDrop
  // cascade in Phase 2) read those derived values from context.
  useEffect(() => {
    setEffectiveDispatcherInputs({
      editedDispatcher: editedOrderFields.dispatcher as string | undefined,
      savedDispatcher: (d as Partial<OrderDetails>).dispatcher,
      savedDispatcherIsGwi: (d as Partial<OrderDetails>).dispatcherIsGwi,
    });
  }, [
    editedOrderFields.dispatcher,
    (d as Partial<OrderDetails>).dispatcher,
    (d as Partial<OrderDetails>).dispatcherIsGwi,
    setEffectiveDispatcherInputs,
  ]);

  // Live count from the trouble tickets panel — drives the sub-tab badge.
  // The `troubleTicketCount` prop, when provided, takes precedence so a
  // parent can force a value (mostly useful for tests / external pre-fetch).
  const [ticketCount, setTicketCount] = useState<number>(0);
  const effectiveTicketCount = troubleTicketCount ?? ticketCount;
  // Open (NEW/PENDING) subset of the same fetch — reddens the TTs tab. Kept
  // separate from the badge on purpose: the badge counts every ticket the pro
  // has ever had, the flag only the ones someone still has to act on.
  const [openTicketCount, setOpenTicketCount] = useState<number>(0);

  // Eagerly fetch the count on mount so the sub-tab badge is visible before
  // the user clicks the Trouble Tickets sub-tab (which would otherwise be the
  // first time TroubleTicketsPanel mounts and fires its own fetch).
  useEffect(() => {
    if (!d.pronumber || isTemplate) return; // templates carry no trouble tickets
    const controller = new AbortController();
    troubleTicketsService
      .listByPronumberWithOpenCount(d.pronumber, controller.signal)
      .then(({ tickets, openCount }) => {
        if (controller.signal.aborted) return;
        setTicketCount(tickets.length);
        setOpenTicketCount(openCount);
      })
      .catch(() => { /* panel will fetch again when opened */ });
    return () => controller.abort();
  }, [d.pronumber, troubleTicketRefreshNonce, isTemplate]);

  // Live count for the Service Failures sub-tab badge, pre-fetched on mount.
  // Seed from the persisted order count so the Comments & Status number and
  // the tab badge don't flash 0 before the SF-list fetch resolves.
  const [serviceFailureCount, setServiceFailureCount] = useState<number>(d.rescheduledCount ?? 0);
  // Bumped by handleRescheduleApplied so both the badge pre-fetch and the
  // ReschedulesPanel re-fetch after the reschedule side effect lands.
  const [serviceFailureRefreshNonce, setServiceFailureRefreshNonce] = useState(0);
  // Repair-order count for the ROs tab badge. Pre-fetched on mount (like TTs /
  // SFs) so the badge shows before the user opens the ROs sub-tab; the panel
  // keeps it in sync via onCountChange once mounted.
  const [repairOrderCount, setRepairOrderCount] = useState<number>(0);
  // Open subset, for the ROs tab flag. The tab lists the pro's own repair
  // orders at any status plus its equipment's OPEN ones, so the badge and the
  // flag are genuinely different numbers.
  const [openRepairOrderCount, setOpenRepairOrderCount] = useState<number>(0);
  // Active sub-tab of the Comments & Trouble Tickets section. Lifted out of
  // TabbedSection so the Service Failures count in the Comments tab can jump
  // straight to the SFs list.
  const { pro: communicationPro } = useCommunicationNavigation();
  const [commentsTab, setCommentsTab] = useState(() => communicationPro === String(d.pronumber) ? 'scheduling-requests' : 'cs');
  useEffect(() => {
    if (communicationPro === String(d.pronumber)) setCommentsTab('scheduling-requests');
  }, [communicationPro, d.pronumber]);
  useEffect(() => {
    if (!d.pronumber || isTemplate) return; // templates carry no service failures
    let cancelled = false;
    schedulingService
      .getReschedulesForOrder(d.pronumber)
      .then((list) => {
        if (!cancelled) setServiceFailureCount(list.length);
      })
      .catch(() => { /* panel will fetch again when opened */ });
    return () => { cancelled = true; };
  }, [d.pronumber, serviceFailureRefreshNonce, isTemplate]);

  useEffect(() => {
    if (!d.pronumber || isTemplate) return; // templates carry no repair orders
    const controller = new AbortController();
    repairOrdersService
      .getByPro(d.pronumber, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        setRepairOrderCount(res.data.length);
        setOpenRepairOrderCount(res.openCount ?? 0);
      })
      .catch(() => { /* panel will fetch again when opened */ });
    return () => controller.abort();
  }, [d.pronumber, isTemplate]);

  // Called from TroubleTicketEditModal when a "Reschedule Requested" ticket
  // is created. Fires the parent's order-details refetch (legs + columns)
  // and bumps the SF nonce so the badge + panel pick up the new reschedule.
  const handleRescheduleApplied = useCallback(() => {
    onProRefresh?.();
    setServiceFailureRefreshNonce((n) => n + 1);
  }, [onProRefresh]);

  // Called when the embedded service failure on a trouble ticket is saved.
  // Creating one only bumps the order's reschedule count (no leg/date change),
  // so a targeted SF-list re-fetch is enough — no full pro refetch. The bump
  // refreshes the badge, the ReschedulesPanel rows, and the Comments & Status
  // count (which reads serviceFailureCount).
  const handleServiceFailureSaved = useCallback(() => {
    setServiceFailureRefreshNonce((n) => n + 1);
  }, []);

  // Pending chassis ID change waiting for triaxle-mismatch confirmation.
  // The actual `chassisId` edit is NOT applied until the user clicks OK on
  // the modal — Cancel discards the typed value entirely (mirrors the
  // legacy drawer at OrderDetailsModalContent.tsx:2617-2638 + 3960-4006).
  const [triaxleConfirm, setTriaxleConfirm] = useState<{
    unit: string;
    triaxle: string;
  } | null>(null);

  // Class 9 reminder — shown when LC flips Haz to Y on a pro whose only hazmat
  // lines are class 9. The flip is refused (flag stays as-is).
  const [class9NoticeOpen, setClass9NoticeOpen] = useState(false);

  // Grounded Info modal — mirrors V1 editgroundedinfo as a read-only list.
  const [groundedOpen, setGroundedOpen] = useState(false);
  const [groundedButtonRect, setGroundedButtonRect] = useState<DOMRect | null>(null);
  const [scacInfoOpen, setScacInfoOpen] = useState(false);
  const [chassisSpecsOpen, setChassisSpecsOpen] = useState(false);
  const groundedInfo = (d as Partial<OrderDetails>).groundedInfo ?? [];

  // Container field validation (ISO 6346 check on blur, advisory only).
  const containerInputRef = useRef<HTMLInputElement>(null);
  const containerValidation = useContainerValidation();
  // Validate silently on order load: highlights + inline message if invalid,
  // but the modal stays closed until the user actually edits the value.
  const { validateInitial: validateContainerInitial } = containerValidation;
  useEffect(() => {
    validateContainerInitial(d.container ?? '', d.moveType);
  }, [d.container, d.moveType, validateContainerInitial]);

  /** Effective value for a field: edited value wins, then details. */
  const fieldValue = <K extends keyof OrderDetails>(
    key: K,
  ): OrderDetails[K] | undefined =>
    (editedOrderFields[key as string] !== undefined
      ? editedOrderFields[key as string]
      : (d as Partial<OrderDetails>)[key]) as OrderDetails[K] | undefined;

  // Export-hazmat preadvise indicator (feature/#33245). On an EXPORT move with
  // Haz='Y', a recorded preadvise date turns the normally-yellow Haz flag GREEN
  // and adds a "Preadvised" tag so LC/CSR can see at a glance the export hazmat
  // has been pre-advised. Reads hazmat via fieldValue so the highlight tracks an
  // in-flight edit. Every other case keeps the standard yellow-when-'Y'.
  const hazPreadvised = isExportHazmatPreadvised({
    moveType: d.moveType,
    hazmat: fieldValue('hazmat'),
    preAdviseDatetime: d.preAdviseDatetime,
  });
  const hazHighlight = hazPreadvised ? 'green' : flagHl(fieldValue('hazmat'));

  /** Tailwind class applied to inputs that conflict with a fresh server load. */
  const conflictClass = (key: keyof OrderDetails): string =>
    conflicts[key as string] ? CONFLICT_CLASS : '';

  // LFD-color gate inputs (see isLfdColorActive). Out-gate reads the in-flight
  // edit buffer so saving Out Gate on the Tracing tab clears the LFD red here
  // immediately; origin-leg driver comes from the saved legs (matches V1's
  // server-side render). '0'/'' both mean "not out-gated".
  const lfdOutGateVal = String(fieldValue('outGateDatetime') ?? '').trim();
  const lfdOutGated = lfdOutGateVal !== '' && lfdOutGateVal !== '0';
  const lfdOriginLeg = (d.legs ?? []).find((l) => l.legnum === d.originateLeg);
  const lfdOriginHasDriver = !!(lfdOriginLeg?.driver && String(lfdOriginLeg.driver).trim() !== '');

  // Live chassis-compatibility errors. Same gating as the save-time check —
  // only computed when the user has edited one of the participating fields,
  // so legacy bad data doesn't surface as red highlights on load.
  const chassisMatrix = useChassisCompatibility();
  const liveChassisErrors = useMemo(() => {
    const relevant =
      editedOrderFields.chassisType !== undefined ||
      editedOrderFields.size !== undefined ||
      editedOrderFields.equipType !== undefined ||
      editedOrderFields.chassisProvider !== undefined ||
      editedOrderFields.isTank !== undefined;
    if (!relevant) return [];
    return validateChassisCompatibility(
      String(fieldValue('chassisType') ?? ''),
      String(fieldValue('size') ?? ''),
      String(fieldValue('equipType') ?? ''),
      String(fieldValue('chassisProvider') ?? ''),
      String(fieldValue('isTank') ?? ''),
      chassisMatrix,
      { isTemplate },
    );
    // fieldValue is recomputed each render off `editedOrderFields` + `d`,
    // so depending on the raw source values is sufficient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editedOrderFields.chassisType,
    editedOrderFields.size,
    editedOrderFields.equipType,
    editedOrderFields.chassisProvider,
    editedOrderFields.isTank,
    d.chassisType,
    d.size,
    d.equipType,
    d.chassisProvider,
    d.isTank,
    chassisMatrix,
    isTemplate,
  ]);

  /** No-op setter used when no editing hook is wired (e.g. tests). */
  const setField = (name: keyof OrderDetails, value: any) => {
    updateOrderField?.(name as string, value);
  };

  // Rolling ("Booking Hold") cross-field cascade — legacy dofunc('rolling') +
  // the fv_ gates, confirmed against the legacy front-end 2026-07-22 / 2026-08-27
  // (Lee):
  //   POOL / ROLLING  → 1st Rcv, Cutoff, and Doc Cutoff are cleared and locked
  //                     (the box is held, so those dates no longer apply).
  //   off POOL/ROLLING → those three dates are restored to what they held before
  //                     the hold blanked them.
  //   ROLLING / BOOKING DISCREPANCY → Rolling Fault is editable (and required on
  //                     save — see validateChanges Rule 2).
  //   ANY change      → Rolling Fault is cleared. It only means something for the
  //                     hold it was picked under, and clearing it is what forces
  //                     the rolled-booking prompt to fire again on ROLLING.
  // The lock is derived at render; the clears/restores fire imperatively on change.
  // Only reachable on EXPORT / RAIL TO PORT (the block these fields live in).
  const rollingValue = s(fieldValue('rolling'));
  const rollingLocksDates = rollingValue === 'POOL' || rollingValue === 'ROLLING';
  const rollingFaultEditable =
    rollingValue === 'ROLLING' || rollingValue === 'BOOKING DISCREPANCY';
  const handleRollingChange = (v: string) => {
    const wasHeld = rollingLocksDates;
    setField('rolling', v);
    // Clear the Fault on every Bkg Hold change, not just on blank.
    setField('rollingFault', '');
    if (v === 'POOL' || v === 'ROLLING') {
      ROLLING_HELD_DATES.forEach((k) => setField(k as keyof OrderDetails, ''));
    } else if (wasHeld) {
      // Coming back off a hold — put the originally-loaded dates back rather than
      // leaving the user three blanks to retype. Dropping the staged edit (rather
      // than writing a remembered value) is what makes this exact: the field
      // re-reads whatever it showed before the hold cleared it.
      ROLLING_HELD_DATES.forEach((k) => editing?.revertOrderField?.(k));
    }
  };
  // Legacy defs.pl:561 — the Rolling Fault dropdown's onchange popped `sub rolling`
  // (Reason / Comments) whenever a fault was picked while Booking
  // Hold was ROLLING. Those three values are prompt-only inputs that sr_rolling
  // reads for the EXPORT ROLLING note/history + the GULF WINDS email, so the host
  // owns the prompt and the save. BOOKING DISCREPANCY doesn't prompt: sr_rolling
  // only fires on ROLLING, so nothing would consume the answers (V1 parity).
  const handleRollingFaultChange = (v: string) => {
    setField('rollingFault', v);
    if (v !== '' && rollingValue === 'ROLLING') onRollingFaultSet?.(v);
  };

  // Available-to-Ship is a STAMP, not a typed field (legacy parity, same shape as
  // the template-approve toggle): checking the box records the current user +
  // browser timestamp into availabletoshipuser / availabletoshipdatetime;
  // unchecking clears both. The datetime/user themselves stay read-only displays.
  // Rule 8 (validateChanges) enforces the cargo trio on save once it's stamped.
  const availToShipUserVal = s(fieldValue('availToShipUser'));
  const availToShipDatetimeVal = s(fieldValue('availToShipDatetime'));
  const availToShipStamped = availToShipUserVal !== '' || availToShipDatetimeVal !== '';
  // Change-all: Avail-to-Ship is a two-field stamp (datetime + user), so a single
  // custom "All" checkbox drives BOTH keys together — BulkFieldCheckbox only maps
  // one key. Both are fanned out through the normal checkedFields path on save.
  const {
    enabled: changeAllEnabled,
    checkedFields: changeAllChecked,
    toggleField: changeAllToggle,
  } = useChangeAll();
  const availToShipUpdateAll =
    changeAllChecked.has('availToShipDatetime') && changeAllChecked.has('availToShipUser');
  const setAvailToShipUpdateAll = (on: boolean) => {
    if (changeAllChecked.has('availToShipDatetime') !== on) changeAllToggle('availToShipDatetime');
    if (changeAllChecked.has('availToShipUser') !== on) changeAllToggle('availToShipUser');
  };
  const toggleAvailToShip = () => {
    if (availToShipStamped) {
      setField('availToShipUser', '');
      setField('availToShipDatetime', '');
    } else {
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const stamp = `${pad(now.getMonth() + 1)}/${pad(now.getDate())}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
      setField('availToShipUser', currentUser);
      setField('availToShipDatetime', stamp);
    }
  };
  // Whether the Available-to-Ship CHECKBOX is live (legacy lib_editrecord gate):
  // write ACL + not a template + movetype in {INBOUND,OUTBOUND,EXPORT,DOMESTIC} +
  // the order is on a domestic dispatch queue (highway/dallasdomestic/brokerage)
  // or has a GWI dispatcher. Separate from the FIELD display (seed): the
  // datetime/user still SHOW read-only on movetypes where the box isn't checkable
  // (e.g. REPO, or an outside-carrier order).
  const availToShipCheckable =
    canEditLc('availToShipDatetime') &&
    !isTemplate &&
    ['INBOUND', 'OUTBOUND', 'EXPORT', 'DOMESTIC'].includes(String(d.moveType ?? '')) &&
    (['highway', 'dallasdomestic', 'brokerage'].includes(
      String(fieldValue('dispatcher') ?? ''),
    ) ||
      !!d.orderIsGwiDispatcher);

  // Cutoff / Doc Cutoff proximity tones — used to tint the whole row (label +
  // date + Y/N) so the "near cutoff" highlight is more visible.
  // V1 parity: once the terminate leg has arrived (t_arrivaldatetime set), the
  // cutoff / doc-cutoff deadline no longer matters — suppress the proximity tint.
  const terminateArrived = !!d.terminateLegArrived;
  const cutoffTone = terminateArrived ? undefined : cutoffProximityTone(fieldValue('cutoffDatetime'));
  const docCutoffTone = terminateArrived ? undefined : cutoffProximityTone(fieldValue('documentCutoffDatetime'));

  // Cancel-pregate button (v1 cancelpregate): only on HOU-BPT / HOU-BCT rail,
  // when a pregate PIN is present. Fires the Navis cancel (prod-gated server-side).
  const pregateRailU = String(fieldValue('rail') ?? '').toUpperCase();
  const isPregateRail = pregateRailU === 'HOU-BPT' || pregateRailU === 'HOU-BCT';
  const [pregateNotice, setPregateNotice] = useState<
    { type: 'success' | 'error'; title: string; message: string } | null
  >(null);
  const [cancelingPregate, setCancelingPregate] = useState<'regular' | 'empty' | null>(null);
  // Reports what the TERMINAL said, not merely that a request left the building.
  // The old version always toasted "Cancellation sent" because the server fired
  // the SOAP call on a background thread and answered before it ran, so a cancel
  // that never happened looked identical to one that did. The server now waits,
  // returns the verdict, and clears the PIN when the appointment is confirmed
  // gone — `cleared` is the cue to re-fetch so the row stops showing a dead PIN.
  const handleCancelPregate = async (pregatetype?: 'empty') => {
    const pin = s(pregatetype === 'empty' ? d.emptypregatepin : d.pregatepin);
    if (!pin || !isPregateRail || d.pronumber == null) return;
    setCancelingPregate(pregatetype ?? 'regular');
    try {
      const res = await ordersService.cancelPregate({
        pronumber: d.pronumber, rail: pregateRailU, pregatepin: pin, pregatetype,
      });
      setPregateNotice({
        type: res.skipped || res.ok ? 'success' : 'error',
        title: 'Cancel Pregate',
        message: res.verdict || (res.ok ? `Cancelled PIN ${pin}.` : 'The terminal did not confirm the cancellation.'),
      });
      if (res.cleared) await onProRefresh?.();
    } catch (err) {
      // Surface the server's reason (missing POHA credentials, terminal
      // unreachable) instead of a generic failure the user cannot act on.
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setPregateNotice({
        type: 'error',
        title: 'Cancel Pregate',
        message: typeof detail === 'string' && detail ? detail : 'Failed to send the cancellation.',
      });
    } finally {
      setCancelingPregate(null);
    }
  };

  // A date/datetime VALUE rendered as a proximity-colored cell: the tone tints
  // the field cell (bg spans the value + its padding, matching the Y/N flag
  // cells and the LC Dashboard Exports board — EXPORTS_PROXIMITY_CLASS), NOT a
  // tint on the picker input itself. When editable the value is a date picker
  // (its own white box) inside the tinted cell; read-only shows the formatted
  // value with the tone's text color. The shared "Cutoff / Cust" & "ETA / LFD"
  // labels stay untinted (they also head the Y/N and ETA fields; legacy tints
  // only the date cell). Used for Cutoff and Last Free Day. `tone` undefined =
  // no color (today's look).
  const renderProximityDateValue = (o: {
    fieldKey: keyof OrderDetails;
    mode: 'date' | 'datetime';
    editable: boolean;
    tone: 'red' | 'orange' | 'yellow' | undefined;
    valueWidth: number;
    ariaLabel: string;
    disabled?: boolean;
  }) => {
    const raw = s(fieldValue(o.fieldKey));
    const toneCls = o.tone ? EXPORTS_PROXIMITY_CLASS[o.tone] : '';
    const traceRing = isTraceHl(o.fieldKey as string)
      ? 'ring-2 ring-yellow-400 dark:ring-yellow-500/50'
      : '';
    return o.editable ? (
      <div
        className={`min-w-0 rounded ${o.tone ? `${toneCls} px-1` : ''} ${conflictClass(o.fieldKey)} ${traceRing}`.trim()}
        style={{ width: o.valueWidth }}
      >
        <DateTimePicker
          disabled={o.disabled}
          mode={o.mode}
          inputVariant="compact"
          selectionType="single"
          {...(o.mode === 'datetime' ? { use24Hour: true } : { autoApply: true })}
          usePortal
          openDirection="down"
          autoFlip
          labelType="none"
          placeholder={o.ariaLabel}
          ariaLabel={o.ariaLabel}
          className="w-full [&_div]:!min-w-0"
          value={o.mode === 'datetime' ? parseMdYHm(raw) : parseMdYDate(raw)}
          onChange={(v) =>
            setField(
              o.fieldKey,
              o.mode === 'datetime'
                ? formatMdYHm(v as Date | null)
                : formatMdYDate(v as Date | null),
            )
          }
        />
      </div>
    ) : (
      <span
        className={`inline-flex items-center h-6 px-1.5 text-[0.75rem] truncate rounded ${o.tone ? toneCls : raw ? 'text-slate-900 dark:text-gray-100' : 'text-slate-400 dark:text-gray-500'} ${traceRing}`.trim()}
        style={{ width: o.valueWidth }}
        aria-label={o.ariaLabel}
        title={isTraceHl(o.fieldKey as string) ? 'Updated by this trace' : raw || undefined}
      >
        {raw || PLACEHOLDER}
      </span>
    );
  };

  // ETA / Revised ETA row — shared by the non-export column and the RAIL TO PORT
  // export column. ETA is legacy `etadate` (date picker); Revised ETA is
  // `revisedetadatetime` (datetime — the same field the Tracing tab edits, so a
  // save from either place reflects in the other). Row visibility follows etadate
  // (config); Revised ETA and LFD each hide independently. LFD sits on its own
  // line beneath, alongside the Storage Cleared link, so the wider datetime
  // picker fits without squeezing the row.
  const renderEtaLfdRow = (labelWidth: number) =>
    !hidden('etadate') ? (
      <>
        <Row>
          <Label
            width={labelWidth}
            historyFields={['etaDate', 'revisedEtaDatetime']}
            value={`${s(fieldValue('etaDate'))} / ${s(fieldValue('revisedEtaDatetime'))}`}
          >
            ETA/REVISED
          </Label>
          <BulkFieldCheckbox fieldName="etaDate" />
          {canEditLc('etaDate') ? (
            <div
              className={`min-w-0 ${conflictClass('etaDate')} ${
                isTraceHl('etaDate') ? 'rounded ring-2 ring-yellow-400 dark:ring-yellow-500/50' : ''
              }`.trim()}
              style={{ width: 140 }}
            >
              <DateTimePicker
                mode="date"
                inputVariant="compact"
                selectionType="single"
                autoApply
                usePortal
                openDirection="down"
                autoFlip
                labelType="none"
                placeholder="ETA"
                className="w-full [&_div]:!min-w-0"
                value={parseMdYDate(s(fieldValue('etaDate')))}
                onChange={(v) => setField('etaDate', formatMdYDate(v as Date | null))}
              />
            </div>
          ) : (
            <ReadValue value={fieldValue('etaDate')} mono width={140} ariaLabel="ETA" />
          )}
          {!hidden('revisedEtaDatetime') && (
            <>
              <BulkFieldCheckbox fieldName="revisedEtaDatetime" />
              {canEditLc('revisedEtaDatetime') ? (
                <div
                  className={`min-w-0 ${conflictClass('revisedEtaDatetime')} ${
                    isTraceHl('revisedEtaDatetime')
                      ? 'rounded ring-2 ring-yellow-400 dark:ring-yellow-500/50'
                      : ''
                  }`.trim()}
                  style={{ width: 160 }}
                >
                  {/* Wider than the date cells (it carries a time too), and
                    [&_div]:!min-w-0 drops the picker's own 180px datetime
                    min-width so this cell still compresses with the row in a
                    narrow column — same treatment as the ETA cell above. */}
                  <DateTimePicker
                    mode="datetime"
                    inputVariant="compact"
                    selectionType="single"
                    use24Hour
                    usePortal
                    openDirection="down"
                    autoFlip
                    labelType="none"
                    placeholder="Revised ETA"
                    ariaLabel="Revised ETA"
                    className="w-full [&_div]:!min-w-0"
                    value={parseMdYHm(s(fieldValue('revisedEtaDatetime')))}
                    onChange={(v) => setField('revisedEtaDatetime', formatMdYHm(v as Date | null))}
                  />
                </div>
              ) : (
                <ReadValue
                  value={fieldValue('revisedEtaDatetime')}
                  mono
                  width={160}
                  ariaLabel="Revised ETA"
                />
              )}
            </>
          )}
        </Row>
        {(!hidden('lastFreeDay') || showStorageCleared) && (
          <Row>
            {/* LFD dropped to its own line so ETA + Revised ETA fit the row; the
              Storage Cleared link rides along beside it. */}
            {!hidden('lastFreeDay') ? (
              <Label
                width={labelWidth}
                historyFields={['lastFreeDay']}
                value={s(fieldValue('lastFreeDay'))}
              >
                LFD
              </Label>
            ) : (
              <div style={{ width: labelWidth }} className="shrink-0" />
            )}
            {!hidden('lastFreeDay') && (
              <>
                <BulkFieldCheckbox fieldName="lastFreeDay" />
                {renderProximityDateValue({
                  fieldKey: 'lastFreeDay',
                  mode: 'date',
                  editable: canEditLc('lastFreeDay'),
                  tone: isLfdColorActive(d.railTraceable, lfdOutGated, lfdOriginHasDriver)
                    ? lfdProximityTone(fieldValue('lastFreeDay'))
                    : undefined,
                  valueWidth: 140,
                  ariaLabel: 'Last Free Day',
                })}
              </>
            )}
            {showStorageCleared && (
              <button
                type="button"
                onClick={(e) => {
                  setScButtonRect(e.currentTarget.getBoundingClientRect());
                  setScPopoverOpen((o) => !o);
                }}
                className={`inline-flex items-center h-6 px-1.5 text-[0.75rem] rounded whitespace-nowrap hover:underline ${d.storageClearedDate
                    ? 'text-brand-red font-medium'
                    : 'text-blue-600 dark:text-blue-400'
                  }`}
                title="View storage cleared history / clear storage"
                aria-label="Storage cleared"
              >
                {d.storageClearedDate ? `Cleared thru ${d.storageClearedDate}` : 'Clear Storage'}
              </button>
            )}
          </Row>
        )}
        {!hidden('examLfd') && (
          <Row>
            <Label width={labelWidth} historyFields={['examLfd']} value={s(fieldValue('examLfd'))}>Exam LFD</Label>
            {renderProximityDateValue({
              fieldKey: 'examLfd', mode: 'date', editable: canEditLc('examLfd'),
              tone: undefined, valueWidth: 140, ariaLabel: 'Exam LFD',
            })}
          </Row>
        )}
      </>
    ) : null;

  // Target Deliv (targetonorby ON/BY selector + targetdeliverydate) on one row,
  // with Cust Target (deliveryoption1date) stacked directly beneath it. All
  // three are LC-editable via the order-lc-fields gate — the caller wraps these
  // rows in <OrderLcFieldGroup>. Shared between the export and non-export
  // column layouts, which differ only in label width (this was previously
  // duplicated, which is how the two slots drifted to both showing custTargetDate).
  const renderTargetDatesRows = (labelWidth: number) => (
    <>
      <Row>
        <Label
          width={labelWidth}
          historyFields={['targetOnOrBy', 'targetDeliveryDate']}
          value={`${s(fieldValue('targetOnOrBy'))} ${s(fieldValue('targetDeliveryDate'))}`.trim()}
        >
          Target Deliv
        </Label>
        <BulkFieldCheckbox fieldName="targetOnOrBy" />
        {canEditLc('targetOnOrBy') ? (
          <div style={{ width: 64 }}>
            <SelectInput
              value={s(fieldValue('targetOnOrBy'))}
              options={[
                { value: '', label: PLACEHOLDER },
                { value: 'ON', label: 'ON' },
                { value: 'BY', label: 'BY' },
              ]}
              onChange={(v) => setField('targetOnOrBy', v)}
              ariaLabel="Target On or By"
              className={conflictClass('targetOnOrBy')}
            />
          </div>
        ) : (
          <ReadValue value={fieldValue('targetOnOrBy')} width={32} ariaLabel="Target On or By" />
        )}
        <BulkFieldCheckbox fieldName="targetDeliveryDate" />
        {canEditLc('targetDeliveryDate') ? (
          <div className={`flex-1 min-w-0 ${conflictClass('targetDeliveryDate')}`.trim()}>
            <DateTimePicker
              mode="date"
              inputVariant="compact"
              selectionType="single"
              autoApply
              usePortal
              openDirection="down"
              autoFlip
              labelType="none"
              placeholder="Target"
              className="w-full [&_div]:!min-w-0"
              value={parseMdYDate(s(fieldValue('targetDeliveryDate')))}
              onChange={(v) => setField('targetDeliveryDate', formatMdYDate(v as Date | null))}
            />
          </div>
        ) : (
          <ReadValue value={fieldValue('targetDeliveryDate')} mono ariaLabel="Target Delivery Date" />
        )}
      </Row>
      <Row>
        <Label width={labelWidth} bulkFieldName="custTargetDate" bulkEditable={canEditLc('custTargetDate')} value={s(fieldValue('custTargetDate'))}>Cust Target</Label>
        {canEditLc('custTargetDate') ? (
          <div className={`flex-1 min-w-0 ${conflictClass('custTargetDate')}`.trim()}>
            <DateTimePicker
              mode="date"
              inputVariant="compact"
              selectionType="single"
              autoApply
              usePortal
              openDirection="down"
              autoFlip
              labelType="none"
              placeholder="Cust Target"
              className="w-full [&_div]:!min-w-0"
              value={parseMdYDate(s(fieldValue('custTargetDate')))}
              onChange={(v) => setField('custTargetDate', formatMdYDate(v as Date | null))}
            />
          </div>
        ) : (
          <ReadValue value={fieldValue('custTargetDate')} mono ariaLabel="Cust Target" />
        )}
      </Row>
    </>
  );

  // Bidirectional lbs/kgs/MT cascade (V1 parity: kgconvert + mtconvert). Typing
  // in ANY of the three recomputes the other two — see weightCascade for the
  // factors and the raw-vs-formatted rules. Customer 4525 (Nexa) uses a
  // higher-precision kg factor.
  const isNexa = d.customer === 4525;
  const applyWeightCascade = (source: WeightField, raw: string) => {
    const patch = weightCascade(source, raw, { isNexa });
    // Not a number yet (partial entry) — stage the keystroke, cascade nothing.
    if (!patch) {
      setField(source, raw);
      return;
    }
    setField(source, patch[source]);
    (['lbs', 'kgs', 'metricTons'] as WeightField[])
      .filter((k) => k !== source)
      .forEach((k) => setField(k, patch[k]));
  };

  // Blur: settle the typed field to the 2 decimals the column stores (and an
  // emptied field back to '0.00' — the value V1 keeps for "no weight").
  const normalizeWeightField = (source: WeightField, raw: string) => {
    const normalized = normalizeWeightEntry(raw);
    if (normalized !== null) setField(source, normalized);
  };

  const dispatcherValue = s(fieldValue('dispatcher'));

  // Carrier is editable only for outside-carrier dispatchers (see
  // CARRIER_DISPATCHERS). `dispatcherValue` already reflects the edited-or-saved
  // dispatcher, so this recomputes as the dispatcher changes and locks the
  // field back to a read-only name once it leaves the set.
  const currentDispatcher = String(dispatcherValue ?? '');
  const carrierEnabled = canEditLc('carrier') && CARRIER_DISPATCHERS.has(currentDispatcher);

  const chassisIdValue = s(fieldValue('chassisId'));

  // Red/green chassis-location pin: green only when the unit is an active GWI
  // equipment-list chassis (`chassisEquipment`) that BB Radar has coordinates
  // for (its `hasRadar` flag). Memoized — the equipment list runs ~3.7k rows and
  // this would otherwise re-scan on every keystroke render.
  const chassisTrackingStatus = useMemo(
    () => getChassisTrackingStatus(chassisIdValue, chassisEquipment),
    [chassisIdValue, chassisEquipment],
  );

  // Scale ⟵ OTR cross-field rule (FIELD_AUDIT resolved spec #1): when OTR is on,
  // Scale is forced to 'Y' and locked. v1 enforces this on save; we mirror it on
  // the form for immediate feedback. Applies to orders and templates alike.
  const otrOn = flag(fieldValue('otr')) === 'Y';

  // ─────────────────────────────────────────────────────────────────────
  // Realtime overweight orchestration (calc + transition toast + system-
  // approval auto-clear + edit-buffer flag write) lives in a shared hook
  // so other order-edit surfaces can reuse it. UI side effects (red
  // highlight on weight inputs, toast portal) consume the result below.
  // ─────────────────────────────────────────────────────────────────────
  const {
    isOverweightForHighlight,
    toast: overweightToast,
    dismissToast: dismissOverweightToast,
  } = useOverweightOrchestrator(d, editing);

  // Rails / terminals — populates the editable Terminal dropdown below
  // and feeds the mode/terminal compatibility check (V1 parity). Fetched
  // once on mount via the session-scoped service cache.
  const [rails, setRails] = useState<readonly RailOption[]>([]);
  useEffect(() => {
    let cancelled = false;
    getRails()
      .then((rows) => { if (!cancelled) setRails(rows); })
      .catch(() => { /* dropdown stays empty + skipped validation */ });
    return () => { cancelled = true; };
  }, []);
  // Steamship-line SCACs for the editable SCAC dropdown. Fetched once on mount
  // (session-cached in the service). The saved value is kept selectable even if
  // it's not in the active list, so an inactive-SCAC order still displays it.
  const [scacs, setScacs] = useState<readonly ScacOption[]>([]);
  useEffect(() => {
    let cancelled = false;
    getScacs()
      .then((rows) => { if (!cancelled) setScacs(rows); })
      .catch(() => { /* dropdown stays empty; field still shows the saved value */ });
    return () => { cancelled = true; };
  }, []);
  const scacValue = s(fieldValue('scac'));
  const scacOptions = useMemo(() => {
    // Alphabetical by SCAC code (the dropdown shows "CODE — Name").
    const sorted = [...scacs].sort((a, b) => a.value.localeCompare(b.value));
    const base = [{ value: '', label: PLACEHOLDER }, ...sorted];
    if (scacValue && !scacs.some((o) => o.value === scacValue)) {
      base.push({ value: scacValue, label: scacValue });
    }
    return base;
  }, [scacs, scacValue]);

  // CST Dispatcher — a "both" field (dispatchers + LCs, no LC gate). Options are
  // the active BROKERAGE-group users; value = username (cstdispatcher column),
  // label = full name. Keep the saved value selectable even if it's not (or no
  // longer) in the brokerage list, labelled with its joined display name.
  const [cstDispatchers, setCstDispatchers] = useState<readonly CstDispatcherOption[]>([]);
  useEffect(() => {
    let cancelled = false;
    getCstDispatchers()
      .then((rows) => { if (!cancelled) setCstDispatchers(rows); })
      .catch(() => { /* dropdown stays empty; field still shows the saved value */ });
    return () => { cancelled = true; };
  }, []);
  const cstDispatcherValue = s(fieldValue('cstDispatcherUsername'));
  const cstDispatcherOptions = useMemo(() => {
    const base = [{ value: '', label: PLACEHOLDER }, ...cstDispatchers];
    if (cstDispatcherValue && !cstDispatchers.some((o) => o.value === cstDispatcherValue)) {
      base.push({ value: cstDispatcherValue, label: s(d.cstDispatcher) || cstDispatcherValue });
    }
    return base;
  }, [cstDispatchers, cstDispatcherValue, d.cstDispatcher]);

  // Driver re-qualification on weight changes is owned by LegsGrid (which
  // hosts DriverSelectorCell). LegsGrid passes our edited fields through to
  // each cell as `revalidationKey`, the cells re-run their existing
  // validation, and LegsGrid aggregates results into editing.setExtraSaveErrors
  // — single validation code path, no duplication here.

  const effectiveRail = String(fieldValue('rail') ?? '');
  // The Mode/Terminal compatibility hard block used to live here (and owned the
  // rail->terminalType map). It moved to useModeTerminalSaveGuard, called from ProDetail:
  // this tab unmounts on a tab switch and took the block with it, so the mismatch saved
  // from anywhere else (bugfix #33440).

  const orderInfoBody = (
    <div className="bg-sunken border border-slate-200 dark:border-gray-700 rounded-md overflow-hidden pd-grid-wrap">
      {/*
        V1 parity hard-block validation banner — order-wide, so it spans the full
        width above all columns (it applies to the whole order, not one field).
        Errors come from the same `validateOrderSave` the save hook calls, so what
        shows here is exactly what will block save. Edit mode only (no `editing`
        prop = read-only view). Driver re-qualification errors are surfaced
        separately by LegsGrid and merged into the save-time toast.
      */}
      {editing && (() => {
        // ONE tier, gated on `validationAttempted`: the banner stays silent until
        // the user actually presses Save/Create and is blocked. From then on it
        // re-runs on every edit — it's a pure computation memoized on the edited
        // state — so each fix drops its line off the list live, with no second
        // click needed.
        //
        // This used to render the local `validateOrderSave` subset BEFORE the
        // first attempt and the hook's full list after. The two tiers never held
        // the same rules, so which problems greeted you on open was an accident
        // of which file a rule happened to live in: chassis type (local) shouted
        // on a freshly-seeded create while container / CSR / Terminal / the
        // Delivery-Order gate (all in validateChanges) waited politely for a
        // click. Announcing everything on open is the wrong cure — a new order is
        // SUPPOSED to start incomplete. So nothing speaks until you ask to save.
        //
        // `validateChanges` is the hook's complete list and a superset of the
        // local subset (it calls validateOrderSave itself). The fallback covers
        // hosts that pass `editing` without it.
        if (!editing.validationAttempted) return null;
        const validationErrors = editing.validateChanges
          ? editing.validateChanges()
          : validateOrderSave(
              editing.editedOrderFields,
              d,
              {
                isTemplate,
                canEditHazmat,
                hazmatLegActive: hazmatInfoLegActive(d, d.legs ?? []),
              },
            );
        if (validationErrors.length === 0) return null;
        return (
          <div
            data-testid="order-save-validation-errors"
            className="mb-2 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-800 rounded-md p-2 text-xs text-red-900 dark:text-red-200 space-y-1"
            role="alert"
          >
            <div className="font-semibold">
              {createMode ? 'Cannot create until resolved:' : 'Cannot save until resolved:'}
            </div>
            {validationErrors.map((e, i) => (
              <div key={i}>• {e}</div>
            ))}
          </div>
        );
      })()}
      <div className="pd-grid">
        {/* COLUMN 1 — People & Equipment */}
        <Section title="People & Equipment" icon={<Icon.user className="w-3.5 h-3.5" />} className="pd-col">
          <Row>
            <Label width={LABEL_W} bulkFieldName="csr" bulkEditable={canEditLc('csr')} value={s(fieldValue('csr'))}>CSR</Label>
            {canEditLc('csr') ? (
              <SelectInput
                // EFFECTIVE value, not `d.csr`. SelectInput mirrors this prop
                // into internal state and renders that, so binding the SAVED
                // value made a staged pick survive only as long as the
                // component stayed mounted: this tab unmounts on a tab switch,
                // and the remount re-seeded the select from the old saved
                // value. The edit itself lives in the hook and was never lost —
                // the save wrote the right CSR — but the field lied about it.
                value={s(fieldValue('csr'))}
                options={[
                  // Keep the saved CSR selectable even if it's not in the
                  // active roster (e.g. a deactivated user).
                  ...(d.csr && !csrs.some((c) => c.value === d.csr)
                    ? [{ value: d.csr, label: s(d.orderLC) || d.csr }]
                    : []),
                  { value: '', label: PLACEHOLDER },
                  ...csrs,
                ]}
                onChange={(v) => setField('csr', v)}
                ariaLabel="CSR"
                className={conflictClass('csr')}
              />
            ) : (
              <ReadValue value={d.orderLC} />
            )}
          </Row>
          <Row>
            <Label width={LABEL_W} bulkFieldName="whseCsrUsername" bulkEditable={canEditLc('whseCsr')} value={s(fieldValue('whseCsrUsername'))}>Whse CSR</Label>
            {canEditLc('whseCsr') ? (
              <SelectInput
                // Effective value — same remount bug as CSR above.
                value={s(fieldValue('whseCsrUsername'))}
                options={[
                  ...(d.whseCsrUsername &&
                    !csrs.some((c) => c.value === d.whseCsrUsername)
                    ? [{ value: d.whseCsrUsername, label: s(d.whseCsr) || d.whseCsrUsername }]
                    : []),
                  { value: '', label: PLACEHOLDER },
                  ...csrs,
                ]}
                onChange={(v) => setField('whseCsrUsername', v)}
                ariaLabel="Whse CSR"
                className={conflictClass('whseCsrUsername')}
              />
            ) : (
              <ReadValue value={d.whseCsr} />
            )}
          </Row>
          <Row>
            <Label width={LABEL_W} bulkFieldName="dispatcher" bulkEditable={canEdit('dispatcher')} value={s(dispatcherValue)}>Dispatcher</Label>
            <SelectInput
              value={dispatcherValue}
              options={[
                // A saved dispatcher missing from the active list (e.g. a deactivated user) is
                // now rendered + orange-flagged centrally by SelectInput's stale-value handling.
                { value: '', label: PLACEHOLDER },
                ...allDispatchers.map((dp) => ({
                  value: dp.username,
                  label: dp.gwi ? dp.displayname : `${dp.displayname} (non-GWI)`,
                })),
              ]}
              onChange={(v) => {
                setField('dispatcher', v);
                // Carrier only applies to outside-carrier dispatchers; clear
                // BOTH the code and the display name when the dispatcher leaves
                // that set (the field also locks back to a read-only, now-empty
                // name via `carrierEnabled`).
                if (!CARRIER_DISPATCHERS.has(v)) {
                  setField('carrierCode', '');
                  setField('carrier', '');
                  setField('carrierScac', '');
                }
              }}
              ariaLabel="Dispatcher"
              disabled={!canEdit('dispatcher')}
              className={conflictClass('dispatcher')}
            />
          </Row>
          <Row>
            <Label width={LABEL_W} bulkFieldName="cstDispatcherUsername" bulkEditable={canEdit('cstDispatcher')} historyFields={['cstDispatcherUsername']} value={s(cstDispatcherValue)}>CST Dispatcher</Label>
            {canEdit('cstDispatcher') ? (
              <SelectInput
                value={cstDispatcherValue}
                options={cstDispatcherOptions}
                onChange={(v) => setField('cstDispatcherUsername', v)}
                ariaLabel="CST Dispatcher"
                className={conflictClass('cstDispatcherUsername' as keyof OrderDetails)}
              />
            ) : (
              <ReadValue value={d.cstDispatcher} ariaLabel="CST Dispatcher" />
            )}
          </Row>
          <Row>
            <Label width={LABEL_W} bulkFieldName="carrierCode" bulkEditable={!!carrierEnabled} value={s(editedOrderFields.carrier ?? d.carrier)}>Carrier</Label>
            {carrierEnabled ? (
              <CarrierLookupField
                value={{
                  recno:
                    Number(editedOrderFields.carrierCode ?? d.carrierCode) || 0,
                  // Persist BOTH the recno (carrierCode) and the display name
                  // (carrier) in editedOrderFields so a picked carrier's name
                  // survives a tab switch / remount. `carrier` is display-only
                  // (not in ORDER_FIELD_COLUMNS) so it persists but isn't saved.
                  name: s(editedOrderFields.carrier ?? d.carrier),
                  // SCAC is display-only too — it drives the outside-carrier
                  // pseudo-driver code but isn't saved.
                  scac: s(editedOrderFields.carrierScac ?? d.carrierScac),
                }}
                onChange={(v) => {
                  setField('carrierCode', v.recno ? String(v.recno) : '');
                  setField('carrier', v.name);
                  setField('carrierScac', v.scac);
                }}
                onAddNew={(term) => {
                  setNewCarrierName(term);
                  setNewCarrierOpen(true);
                }}
              />
            ) : (
              <ReadValue value={d.carrier} />
            )}
            {newCarrierOpen && (
              <Suspense fallback={null}>
                <CarrierProfileEditDrawer
                  row={null}
                  initialName={newCarrierName}
                  onClose={() => setNewCarrierOpen(false)}
                  onSaved={() => {}}
                  onCreated={(c) => {
                    // Auto-link the freshly created carrier onto the order
                    // (same fields a pick sets: code + display name + SCAC).
                    setField('carrierCode', c.recno ? String(c.recno) : '');
                    setField('carrier', c.truckername);
                    setField('carrierScac', c.truckerscac);
                    setNewCarrierOpen(false);
                  }}
                />
              </Suspense>
            )}
          </Row>
          <Row>
            <Label
              width={LABEL_W}
              historyFields={['size', 'equipType']}
              value={`${s(fieldValue('size'))} / ${s(fieldValue('equipType'))}`}
            >
              {s(fieldValue('equipType')) === 'VAN' ? 'Size / Type / GWI' : 'Size / Type'}
            </Label>
            <BulkFieldCheckbox fieldName="size" />
            <SelectInput
              value={s(fieldValue('size'))}
              options={EQUIPMENT_SIZES.map((sz) => ({ value: sz, label: sz || '-' }))}
              width={64}
              disabled={!canEditLc('size')}
              onChange={(v) => setField('size', v)}
              ariaLabel="Equipment size"
              highlight={chassisErrorsContainField(liveChassisErrors, 'size') ? 'red' : undefined}
              className={conflictClass('size')}
            />
            <BulkFieldCheckbox fieldName="equipType" />
            <SelectInput
              value={s(fieldValue('equipType'))}
              options={EQUIPMENT_TYPES.map((et) => ({ value: et, label: et || '-' }))}
              disabled={!canEdit('equipType')}
              onChange={(v) => setField('equipType', v)}
              ariaLabel="Equipment type"
              highlight={chassisErrorsContainField(liveChassisErrors, 'equipType') ? 'red' : undefined}
              className={conflictClass('equipType')}
            />
            {s(fieldValue('equipType')) === 'VAN' && (
              <BulkFieldCheckbox fieldName="isGwiEquip" />
            )}
            {s(fieldValue('equipType')) === 'VAN' && (
              <SelectInput
                value={s(fieldValue('isGwiEquip'))}
                options={[
                  { value: 'N', label: 'N' },
                  { value: 'Y', label: 'Y' },
                ]}
                width={48}
                disabled={!canEdit('isGwiEquip')}
                onChange={(v) => setField('isGwiEquip', v)}
                ariaLabel="GWI equipment flag"
                className={conflictClass('isGwiEquip')}
              />
            )}
          </Row>
          {(() => {
            const sourceContainer = (d.container ?? '').trim();
            const isExportLike = d.moveType === 'EXPORT' || d.moveType === 'RAIL TO PORT';
            const showRsvBadge = isExportLike && sourceContainer !== '';
            // Container is locked whenever it's being reused from another order —
            // applies to any non-IMPORT movetype (IMPORT initiates reuse;
            // everyone else consumes it).
            const containerReadOnly =
              sourceContainer !== '' && !!d.reuseContainerPro && d.moveType !== 'IMPORT';
            // Equipment lookup applies on GWI VAN orders: equipType is exactly
            // 'VAN' and the order is flagged isGwiEquip. Uses effective values
            // so the lookup reacts as soon as the user edits Type / GWI in
            // this same form.
            const effectiveEquipType = s(fieldValue('equipType'));
            const effectiveIsGwiEquip = s(fieldValue('isGwiEquip'));
            const containerSearchEnabled =
              !containerReadOnly &&
              canEdit('container') &&
              effectiveEquipType === 'VAN' &&
              effectiveIsGwiEquip === 'Y';
            const warnedBorder = containerValidation.warned
              ? 'bg-red-100 dark:bg-red-900/40 border-red-600 text-red-800 dark:text-red-200 font-semibold'
              : '';
            // Flex booking is an EXPORT-only Y/N flag rendered inline with
            // the container input. moveType isn't editable, so the saved
            // value drives visibility.
            // flexbooking is config-governed (shown EXPORT + RAIL TO PORT per the
            // seed; was EXPORT-only here). Drives the "/ Flex" label + the control.
            const showFlexBooking = !hidden('flexBooking');
            return (
              <Row>
                <Label
                  width={LABEL_W}
                  historyFields={['container']}
                  value={s(fieldValue('container'))}
                >
                  {showFlexBooking ? 'Equipment # / Flex' : 'Equipment #'}
                </Label>
                <BulkFieldCheckbox fieldName="container" />
                {canEdit('container') ? (
                  <EquipmentLookup
                    ref={containerInputRef}
                    value={s(fieldValue('container'))}
                    onChange={(v) => {
                      containerValidation.clearWarning();
                      setField('container', v.toUpperCase());
                    }}
                    equipment={vanEquipment}
                    searchEnabled={containerSearchEnabled}
                    readOnly={containerReadOnly}
                    onBlur={(v) => containerValidation.validate(v, d.moveType)}
                    wrapperClassName="flex-1 min-w-0"
                    inputClassName={
                      'w-full min-w-0 bg-white dark:bg-gray-900 ' +
                      'border border-slate-300 dark:border-gray-600 rounded ' +
                      'px-1.5 py-[3px] text-[0.75rem] text-slate-900 ' +
                      `dark:text-gray-100 h-6 ${warnedBorder} ${conflictClass('container')}`
                    }
                    placeholder={PLACEHOLDER}
                    ariaLabel="Equipment number (container)"
                  />
                ) : (
                  <ReadValue
                    value={s(fieldValue('container'))}
                    mono
                    ariaLabel="Equipment number (container)"
                  />
                )}
                {showFlexBooking && (
                  <BulkFieldCheckbox fieldName="flexBooking" />
                )}
                {showFlexBooking && (
                  <SelectInput
                    value={s(fieldValue('flexBooking'))}
                    options={[
                      { value: 'N', label: 'N' },
                      { value: 'Y', label: 'Y' },
                    ]}
                    width={48}
                    disabled={!canEditLc('flexBooking')}
                    onChange={(v) => setField('flexBooking', v)}
                    ariaLabel="Flex booking"
                    className={conflictClass('flexBooking')}
                  />
                )}
                {(d.inbondType ?? '').trim() !== '' && <ContainerBadge>IN BOND</ContainerBadge>}
                {d.isExam === 'Y' && (d.examReleaseDate ?? '').trim() === '' && (
                  <ContainerBadge>CES</ContainerBadge>
                )}
                {showRsvBadge && <ContainerBadge>RSV</ContainerBadge>}
              </Row>
            );
          })()}
          {containerValidation.warned && (
            <div
              className="text-[0.625rem] text-red-600 dark:text-red-300 leading-tight -mt-0.5 mb-1"
              style={{ paddingLeft: LABEL_W + 8 }}
            >
              Container format invalid
            </div>
          )}
          <Row
            className={
              s(fieldValue('chassisProvider')) === 'GWI' ? 'bg-yellow-300 rounded px-1' : ''
            }
          >
            <Label
              width={LABEL_W}
              historyFields={['chassisProvider', 'chassisType']}
              value={`${s(fieldValue('chassisProvider'))} / ${s(fieldValue('chassisType'))}`}
            >
              Pool / Chass
            </Label>
            <BulkFieldCheckbox fieldName="chassisProvider" />
            <SelectInput
              value={s(fieldValue('chassisProvider'))}
              options={[
                { value: '', label: PLACEHOLDER },
                ...chassisProviders.map((cp) => ({ value: cp, label: cp })),
              ]}
              width={62}
              onChange={(v) => setField('chassisProvider', v)}
              ariaLabel="Chassis provider"
              disabled={!canEdit('chassisProvider')}
              highlight={chassisErrorsContainField(liveChassisErrors, 'chassisProvider') ? 'red' : undefined}
              className={conflictClass('chassisProvider')}
            />
            <BulkFieldCheckbox fieldName="chassisType" />
            <SelectInput
              value={s(fieldValue('chassisType'))}
              options={CHASSIS_TYPES as string[]}
              onChange={(v) => setField('chassisType', v)}
              ariaLabel="Chassis type"
              disabled={!canEdit('chassisType')}
              highlight={chassisErrorsContainField(liveChassisErrors, 'chassisType') ? 'red' : undefined}
              className={conflictClass('chassisType')}
            />
          </Row>
          {liveChassisErrors.length > 0 && (
            <div
              className="text-[0.65625rem] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border border-red-300 dark:border-red-700 rounded px-2 py-1 my-0.5"
              role="alert"
            >
              <ul className="list-disc pl-4 space-y-0.5">
                {liveChassisErrors.map((err, i) => (
                  <li key={i}>{err.message}</li>
                ))}
              </ul>
            </div>
          )}
          <Row>
            <Label width={LABEL_W} bulkFieldName="chassisId" bulkEditable={canEdit('chassisId')} value={chassisIdValue}>Chass #</Label>
            {(() => {
              const effectiveProvider =
                editedOrderFields.chassisProvider !== undefined
                  ? String(editedOrderFields.chassisProvider ?? '')
                  : String(d.chassisProvider ?? '');
              const isGwi = effectiveProvider.toUpperCase() === 'GWI';

              const handleChassisSelect = (item: EquipmentLookupItem) => {
                const effectiveChassisType =
                  editedOrderFields.chassisType !== undefined
                    ? String(editedOrderFields.chassisType ?? '')
                    : String(d.chassisType ?? '');
                const orderHasTriaxle = effectiveChassisType
                  .toLowerCase()
                  .includes('triaxle');
                const chassisIsTriaxle = item.triaxle === 'Y';
                if (orderHasTriaxle !== chassisIsTriaxle) {
                  setTriaxleConfirm({ unit: item.unit, triaxle: item.triaxle ?? 'N' });
                } else {
                  setField('chassisId', item.unit);
                }
              };

              return (
                <>
                  {canEdit('chassisId') ? (
                    <EquipmentLookup
                      value={chassisIdValue}
                      onChange={(v) => setField('chassisId', v)}
                      equipment={chassisEquipment}
                      searchEnabled={isGwi}
                      onSelectItem={handleChassisSelect}
                      showTriaxleColumn
                      wrapperClassName="flex-1 min-w-0"
                      inputClassName={
                        'w-full min-w-0 bg-white dark:bg-gray-900 ' +
                        'border border-slate-300 dark:border-gray-600 rounded ' +
                        'px-1.5 py-[3px] text-[0.75rem] text-slate-900 ' +
                        `dark:text-gray-100 h-6 ${conflictClass('chassisId')}`
                      }
                      placeholder={PLACEHOLDER}
                      ariaLabel="Chassis number"
                    />
                  ) : (
                    <ReadValue value={chassisIdValue} mono ariaLabel="Chassis number" />
                  )}
                  {isGwi && chassisIdValue.trim() !== '' && (
                    <ChassisLocationButton
                      unit={chassisIdValue}
                      status={chassisTrackingStatus}
                      className="shrink-0"
                    />
                  )}
                  {(() => {
                    const isAlert = isChassisSpecsAlert({
                      ooglegal: String(fieldValue('ooglegal') ?? ''),
                      brokerout: String(fieldValue('brokerout') ?? ''),
                      oversizePermitRequired: String(fieldValue('oversizePermitRequired') ?? ''),
                      overweightPermitRequired: String(fieldValue('overweightPermitRequired') ?? ''),
                      oog: String(fieldValue('oog') ?? ''),
                    });
                    return (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setChassisSpecsOpen(true); }}
                        className={
                          'p-0.5 shrink-0 cursor-pointer transition-colors ' +
                          (isAlert
                            ? 'text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300'
                            : 'text-slate-400 hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300')
                        }
                        title="View Size Specifications"
                        aria-label="View Size Specifications"
                      >
                        <Icon.rulerTriangle className="w-4 h-4" />
                      </button>
                    );
                  })()}
                </>
              );
            })()}
          </Row>
          {/* BOL/Booking/Ref # and SCAC each get their own full-width row. */}
          <Row>
            <Label width={LABEL_W} bulkFieldName="bookingNumber" bulkEditable={canEditLc('bookingNumber')} historyFields={['bookingNumber']} value={s(fieldValue('bookingNumber'))}>{bolLabel}</Label>
            {canEditLc('bookingNumber') ? (
              <TextInput
                value={s(fieldValue('bookingNumber'))}
                onChange={(v) => setField('bookingNumber', v)}
                ariaLabel={bolLabel}
                className={conflictClass('bookingNumber')}
                mono
              />
            ) : (
              <ReadValue value={d.bookingNumber} mono ariaLabel={bolLabel} />
            )}
          </Row>
          {!hidden('scac') && (
            <Row>
              <Label width={LABEL_W} bulkFieldName="scac" bulkEditable={canEditLc('scac')} value={scacValue}>SCAC</Label>
              {canEditLc('scac') ? (
                <SingleSelect
                  value={scacValue}
                  options={scacOptions.filter((o) => o.value !== '')}
                  onChange={(v) => setField('scac', v)}
                  enableSearch
                  size="sm"
                  usePortal
                  showClearOption
                  clearOptionLabel={PLACEHOLDER}
                  placeholder={PLACEHOLDER}
                  pinSelectedTop
                  ariaLabel="SCAC"
                  className={`w-44 ${conflictClass('scac')}`.trim()}
                />
              ) : (
                <ReadValue value={d.scac} ariaLabel="SCAC" />
              )}
              {scacTraceControl}
              {!!scacValue.trim() && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setScacInfoOpen(true);
                  }}
                  className="p-0.5 shrink-0 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer transition-colors"
                  title="SCAC Contact Info"
                  aria-label="SCAC Contact Info"
                >
                  <Icon.info className="w-4 h-4" />
                </button>
              )}
            </Row>
          )}
          {/* Return equipment — Domestic moves only (V1 "return equip for
              Domestic moves"). returnpronumber links a return move; the search
              icon opens it in a new tab (same pattern as the Reuse Pro links). */}
          {!hidden('returnPronumber') && (
            <>
              <Row>
                <Label width={LABEL_W} bulkFieldName="returnPronumber" bulkEditable={canEdit('returnPronumber')} value={s(fieldValue('returnPronumber'))}>Return Pro #</Label>
                {(() => {
                  const raw = fieldValue('returnPronumber');
                  const proStr =
                    raw != null && String(raw).trim() !== '' && Number(raw) !== 0
                      ? String(raw)
                      : '';
                  return (
                    <>
                      {canEdit('returnPronumber') ? (
                        <TextInput
                          value={proStr}
                          placeholder={PLACEHOLDER}
                          onChange={(v) =>
                            setField('returnPronumber', v.replace(/[^0-9]/g, ''))
                          }
                          ariaLabel="Return Pro number"
                          className={conflictClass('returnPronumber')}
                        />
                      ) : (
                        <ReadValue value={proStr} ariaLabel="Return Pro number" />
                      )}
                      {proStr !== '' && (
                        <a
                          href={proDetailPath(proStr)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-0.5 shrink-0 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer transition-colors"
                          title="Open return Pro"
                          aria-label="Open return Pro"
                        >
                          <Icon.search className="w-4 h-4" />
                        </a>
                      )}
                    </>
                  );
                })()}
              </Row>
              <Row>
                <Label width={LABEL_W} bulkFieldName="returnEquipment" bulkEditable={canEditLc('returnEquipment')} value={s(fieldValue('returnEquipment'))}>Return Equip #</Label>
                {canEditLc('returnEquipment') ? (
                  <TextInput
                    value={s(fieldValue('returnEquipment'))}
                    placeholder={PLACEHOLDER}
                    onChange={(v) => setField('returnEquipment', v.toUpperCase())}
                    ariaLabel="Return Equipment number"
                    className={conflictClass('returnEquipment')}
                  />
                ) : (
                  <ReadValue
                    value={s(fieldValue('returnEquipment'))}
                    ariaLabel="Return Equipment number"
                  />
                )}
              </Row>
              <Row>
                <Label width={LABEL_W} bulkFieldName="returnChassis" bulkEditable={canEditLc('returnChassis')} value={s(fieldValue('returnChassis'))}>Return Chass #</Label>
                {canEditLc('returnChassis') ? (
                  <TextInput
                    value={s(fieldValue('returnChassis'))}
                    placeholder={PLACEHOLDER}
                    onChange={(v) => setField('returnChassis', v.toUpperCase())}
                    ariaLabel="Return Chassis number"
                    className={conflictClass('returnChassis')}
                  />
                ) : (
                  <ReadValue
                    value={s(fieldValue('returnChassis'))}
                    ariaLabel="Return Chassis number"
                  />
                )}
              </Row>
            </>
          )}
        </Section>

        {/* COLUMN 2 — Move Flags & Schedule */}
        <Section
          title="Move Flags & Schedule"
          icon={<Icon.truck className="w-3.5 h-3.5" />}
          className="pd-col"
        >
          <Row>
            <Label width={LABEL_W}>Mileage Band</Label>
            <ReadValue value={mileageBandLabel(d.mileageBand)} ariaLabel="Mileage band (one-way delivery miles)" />
          </Row>
          <div className="grid grid-cols-2 gap-x-1.5">
            {/* Editable Y/N flags when `editing` is wired. OTR + Daycab feed
                back into the overweight calc (already in its dep array),
                Drop is mirrored to the delivery leg's legDrop via the
                hook. Hot / Swing / Haz / Railbill stay read-only — they
                drive other workflows we haven't opened up yet. */}
            <FlagInput
              label="Daycab"
              bulkFieldName="isDaycab"
              value={flag(fieldValue('isDaycab'))}
              readOnly={!canEdit('isDaycab')}
              onChange={(v) => setField('isDaycab', v)}
              highlight={flagHl(fieldValue('isDaycab'))}
            />
            <FlagInput
              label="Scale"
              bulkFieldName="scale"
              // Forced 'Y' + locked while OTR is on (Scale ⟵ OTR rule).
              value={otrOn ? 'Y' : flag(fieldValue('scale'))}
              readOnly={!canEditLc('scale') || otrOn}
              onChange={(v) => setField('scale', v)}
              highlight={flagHl(otrOn ? 'Y' : fieldValue('scale'))}
            />
            <FlagInput
              label="OTR"
              bulkFieldName="otr"
              value={flag(fieldValue('otr'))}
              readOnly={!canEdit('otr')}
              onChange={(v) => {
                setField('otr', v);
                // Keep Scale in lockstep with OTR: turning OTR on forces Scale to
                // 'Y' (locked to match, so the saved payload agrees with the
                // display); turning it back off drops Scale to 'N' as the now-
                // editable field's starting point, saving the user a click. (#32991)
                setField('scale', v === 'Y' ? 'Y' : 'N');
              }}
              highlight={flagHl(fieldValue('otr'))}
            />
            <FlagInput
              label="Tank"
              bulkFieldName="isTank"
              value={flag(fieldValue('isTank'))}
              readOnly={!canEdit('isTank')}
              onChange={(v) => setField('isTank', v)}
              highlight={flagHl(fieldValue('isTank'))}
            />
            <FlagInput
              label="Hot"
              bulkFieldName="hot"
              value={flag(fieldValue('hot'))}
              readOnly={!canEditLc('hot')}
              onChange={(v) => setField('hot', v)}
              highlight={flagHl(fieldValue('hot'))}
            />
            {!hidden('swing') && (
              <FlagInput
                label="Swing"
                bulkFieldName="swing"
                value={flag(fieldValue('swing'))}
                readOnly={!canEditLc('swing')}
                onChange={(v) => setField('swing', v)}
                highlight={flagHl(fieldValue('swing'))}
              />
            )}
            {/* Haz Y/N is LC-editable (order-lc-fields grouping, same gate as
                Drop / Ports). The line add/delete endpoints also auto-manage this
                flag; this lets an LC user override it directly. `canEditHazmat`
                (which gates the Rule 6 hazmat save-block) resolves the same
                order-lc-fields level, so "field editable" and enforcement stay in
                lockstep — see useCanEditHazmat. */}
            <FlagInput
              label="Haz"
              bulkFieldName="hazmat"
              value={flag(fieldValue('hazmat'))}
              readOnly={!canEditLc('hazmat')}
              onChange={(v) => {
                // Class 9 is hazardous on water only — refuse Y when every
                // hazmat line on the pro is class 9 (INC-24768 lineage).
                if (v === 'Y' && isClass9Only(d.hazmatInfo)) {
                  setClass9NoticeOpen(true);
                  return;
                }
                setField('hazmat', v);
              }}
              highlight={hazHighlight}
              suffix={
                hazPreadvised ? (
                  <span className="text-[0.6875rem] font-semibold text-black whitespace-nowrap">
                    Preadvised
                  </span>
                ) : undefined
              }
            />
            <FlagInput
              label="Drop"
              bulkFieldName="isDrop"
              value={flag(fieldValue('isDrop'))}
              readOnly={!canEditLc('isDrop')}
              onChange={(v) => setField('isDrop', v)}
              highlight={flagHl(fieldValue('isDrop'))}
            />
            {/* Railbill Y/N — dispatcher-editable via its own railbill-flag
                component permission (the container anchors the config cog +
                self-registers it). defaultDisabled keeps it read-only until an
                admin grants edit; templates never railbill, so stay read-only. */}
            {!hidden('railbilled') && (
              <PermissionsContainer
                componentId={RAILBILL_FIELD_COMPONENT_ID}
                componentName="Railbill Flag"
                defaultDisabled
              >
                <FlagInput
                  label="Railbill"
                  bulkFieldName="railbilled"
                  value={flag(fieldValue('railbilled'))}
                  readOnly={isTemplate || !canEditRailbillField}
                  onChange={(v) => setField('railbilled', v)}
                  highlight={flagHl(fieldValue('railbilled'))}
                />
              </PermissionsContainer>
            )}
            <span />
          </div>

          <div className="h-1.5" />

          {isExport ? (
            <>
              {!hidden('rail') && (
                <Row>
                  <Label width={96} bulkFieldName="rail" bulkEditable={canEdit('rail')} value={effectiveRail}>{isRailToPort ? 'Rail Trace' : 'Terminal'}</Label>
                  {/* Terminal = orderscurrent.rail (V1 column name). Labelled "Rail
                    Trace" on RAIL TO PORT, where a "Port Trace" (porttrace) field
                    follows below. Searchable SingleSelect when editable; a
                    ReadValue when read-only or the rails fetch hasn't returned. */}
                  {canEdit('rail') && rails.length > 0 ? (
                    <SingleSelect
                      value={effectiveRail}
                      options={rails.map((r) => ({ value: r.terminal, label: r.terminal }))}
                      onChange={(v) => setField('rail', v)}
                      enableSearch
                      size="sm"
                      usePortal
                      showClearOption
                      clearOptionLabel={PLACEHOLDER}
                      placeholder={PLACEHOLDER}
                      pinSelectedTop
                      ariaLabel={isRailToPort ? 'Rail Trace' : 'Terminal'}
                      className={`w-44 ${conflictClass('rail' as keyof OrderDetails)}`.trim()}
                    />
                  ) : (
                    <ReadValue value={effectiveRail} ariaLabel={isRailToPort ? 'Rail Trace' : 'Terminal'} />
                  )}
                  {terminalTraceControl}
                </Row>
              )}
              {isRailToPort && (
                <Row>
                  <Label width={96} bulkFieldName="portTrace" bulkEditable={canEdit('rail')} value={s(fieldValue('portTrace'))}>Port Trace</Label>
                  {canEdit('rail') && rails.length > 0 ? (
                    <SelectInput
                      value={s(fieldValue('portTrace'))}
                      options={[
                        { value: '', label: PLACEHOLDER },
                        ...rails.map((r) => ({ value: r.terminal, label: r.terminal })),
                      ]}
                      onChange={(v) => setField('portTrace', v)}
                      ariaLabel="Port Trace"
                      className={conflictClass('portTrace' as keyof OrderDetails)}
                    />
                  ) : (
                    <ReadValue value={s(fieldValue('portTrace'))} ariaLabel="Port Trace" />
                  )}
                </Row>
              )}
              {renderEtaLfdRow(96)}
              {!hidden('outGateDatetime') && (
                <Row>
                  <Label width={96}>Out Gate</Label>
                  <ReadValue value={fieldValue('outGateDatetime')} mono ariaLabel="Out Gate" />
                </Row>
              )}
              {!hidden('firstRcv') && (
                <Row>
                  <Label width={96} bulkFieldName="firstRcv" bulkEditable={canEditLc('firstRcv')} historyFields={['firstRcv']} value={s(fieldValue('firstRcv'))}>1st Rcv</Label>
                  {canEditLc('firstRcv') && !rollingLocksDates ? (
                    <div className={`flex-1 min-w-0 ${conflictClass('firstRcv')}`.trim()}>
                      <DateTimePicker
                        mode="date"
                        inputVariant="compact"
                        selectionType="single"
                        autoApply
                        usePortal
                        openDirection="down"
                        autoFlip
                        labelType="none"
                        placeholder="1st Rcv"
                        className="w-full [&_div]:!min-w-0"
                        value={parseMdYDate(s(fieldValue('firstRcv')))}
                        onChange={(v) => setField('firstRcv', formatMdYDate(v as Date | null))}
                      />
                    </div>
                  ) : (
                    <ReadValue value={fieldValue('firstRcv')} mono ariaLabel="First Received" />
                  )}
                </Row>
              )}
              {!hidden('documentCutoffDatetime') && (
                <Row className={proximityRowCls(docCutoffTone)}>
                  <Label
                    width={96}
                    className={proximityLabelCls(docCutoffTone)}
                    historyFields={['documentCutoffDatetime', 'custDocCut']}
                    value={`${s(fieldValue('documentCutoffDatetime'))} / ${s(fieldValue('custDocCut'))}`}
                  >
                    Doc Cut / Cust
                  </Label>
                  <BulkFieldCheckbox fieldName="documentCutoffDatetime" />
                  {renderProximityDateValue({
                    fieldKey: 'documentCutoffDatetime',
                    mode: 'datetime',
                    editable: canEditLc('documentCutoffDatetime') && !rollingLocksDates,
                    tone: docCutoffTone,
                    valueWidth: 170,
                    ariaLabel: 'Document Cutoff',
                  })}
                  {/* Cust field here is Customer DOCUMENT Cutoff (repurposed dvirlights),
                    NOT customerCutoff — they're two separate LC Y/N fields. */}
                  {canEditLc('custDocCut') && <BulkFieldCheckbox fieldName="custDocCut" />}
                  {canEditLc('custDocCut') ? (
                    <SelectInput
                      value={s(fieldValue('custDocCut'))}
                      options={['Y', 'N']}
                      width={48}
                      onChange={(v) => setField('custDocCut', v)}
                      ariaLabel="Customer Document Cutoff"
                    />
                  ) : (
                    <ReadValue value={s(d.custDocCut)} width={32} ariaLabel="Customer Document Cutoff" />
                  )}
                </Row>
              )}
              {!hidden('cutoffDatetime') && (
                <Row className={proximityRowCls(cutoffTone)}>
                  <Label
                    width={96}
                    className={proximityLabelCls(cutoffTone)}
                    historyFields={['cutoffDatetime', 'customerCutoff']}
                    value={`${s(fieldValue('cutoffDatetime'))} / ${s(fieldValue('customerCutoff'))}`}
                  >
                    Cutoff / Cust
                  </Label>
                  <BulkFieldCheckbox fieldName="cutoffDatetime" />
                  {renderProximityDateValue({
                    fieldKey: 'cutoffDatetime',
                    mode: 'datetime',
                    editable: canEditLc('cutoffDatetime') && !rollingLocksDates,
                    tone: cutoffTone,
                    valueWidth: 170,
                    ariaLabel: 'Cutoff',
                  })}
                  <BulkFieldCheckbox fieldName="customerCutoff" />
                  {canEditLc('customerCutoff') ? (
                    <SelectInput
                      value={s(fieldValue('customerCutoff'))}
                      options={['Y', 'N']}
                      width={48}
                      onChange={(v) => setField('customerCutoff', v)}
                      ariaLabel="Customer Cutoff"
                    />
                  ) : (
                    <ReadValue value={d.customerCutoff} width={32} ariaLabel="Customer Cutoff" />
                  )}
                </Row>
              )}
              {!hidden('rolling') && (
                <Row>
                  <Label
                    width={96}
                    historyFields={['rolling', 'rollingFault']}
                    value={`${s(rollingValue)} / ${s(fieldValue('rollingFault'))}`}
                  >
                    Bkg Hold / Fault
                  </Label>
                  <BulkFieldCheckbox fieldName="rolling" />
                  {canEditLc('rolling') ? (
                    <SelectInput
                      value={rollingValue}
                      options={['', 'BOOKING DISCREPANCY', 'POOL', 'ROLLING']}
                      width={100}
                      onChange={handleRollingChange}
                      ariaLabel="Booking Hold"
                      className={conflictClass('rolling')}
                    />
                  ) : (
                    <ReadValue value={d.rolling} width={100} ariaLabel="Booking Hold" />
                  )}
                  <BulkFieldCheckbox fieldName="rollingFault" />
                  {canEditLc('rollingFault') && rollingFaultEditable ? (
                    <SelectInput
                      value={s(fieldValue('rollingFault'))}
                      options={['', 'GWI', 'CUSTOMER', 'FORWARDER/BROKER', 'LOADING WHSE', 'OTHER CARRIER', 'SSLINE']}
                      onChange={handleRollingFaultChange}
                      ariaLabel="Rolling Fault"
                      className={conflictClass('rollingFault')}
                    />
                  ) : (
                    <ReadValue value={fieldValue('rollingFault')} ariaLabel="Rolling Fault" />
                  )}
                </Row>
              )}
              {!hidden('availableGoodToGoDatetime') && (
                <Row>
                  <Label width={96}>Available</Label>
                  <ReadValue value={d.availableGoodToGoDatetime} mono ariaLabel="Available Good to Go" />
                </Row>
              )}
              {!hidden('availToShipDatetime') && (
                <Row>
                  <Label
                    width={96}
                    historyFields={['availToShipDatetime']}
                    value={`${availToShipDatetimeVal} ${availToShipUserVal}`.trim()}
                  >
                    Avail To Ship
                  </Label>
                  {availToShipCheckable && (
                    <label className="inline-flex items-center h-6 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={availToShipStamped}
                        onChange={toggleAvailToShip}
                        aria-label="Mark available to ship"
                        className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
                      />
                    </label>
                  )}
                  {changeAllEnabled && availToShipCheckable && (
                    <label
                      className="inline-flex items-center gap-0.5 h-6 cursor-pointer select-none"
                      title="Apply Available to Ship (date + user) — set or cleared — to every pro in this list"
                    >
                      <input
                        type="checkbox"
                        checked={availToShipUpdateAll}
                        onChange={() => setAvailToShipUpdateAll(!availToShipUpdateAll)}
                        aria-label="Apply available to ship to all pros"
                        className="h-3.5 w-3.5 shrink-0 cursor-pointer accent-blue-600"
                      />
                      <span className="text-[0.625rem] font-semibold text-blue-600 dark:text-blue-400">All</span>
                    </label>
                  )}
                  <ReadValue value={availToShipDatetimeVal} mono width={120} ariaLabel="Available to Ship" />
                  <ReadValue value={availToShipUserVal} ariaLabel="Available to Ship User" />
                </Row>
              )}
              <OrderLcFieldGroup isTemplate={isTemplate}>
                {renderTargetDatesRows(96)}
              </OrderLcFieldGroup>
            </>
          ) : (
            <>
              <Row>
                {!hidden('rail') ? (
                  <div className="w-1/2 flex items-center gap-2 min-w-0">
                    <Label width={LABEL_W_MID} bulkFieldName="rail" bulkEditable={canEdit('rail')} value={effectiveRail}>Terminal</Label>
                    {canEdit('rail') && rails.length > 0 ? (
                      <SingleSelect
                        value={effectiveRail}
                        options={rails.map((r) => ({ value: r.terminal, label: r.terminal }))}
                        onChange={(v) => setField('rail', v)}
                        enableSearch
                        size="sm"
                        usePortal
                        showClearOption
                        clearOptionLabel={PLACEHOLDER}
                        placeholder={PLACEHOLDER}
                        pinSelectedTop
                        ariaLabel="Terminal"
                        className={`w-44 ${conflictClass('rail' as keyof OrderDetails)}`.trim()}
                      />
                    ) : (
                      <ReadValue value={effectiveRail} ariaLabel="Terminal" />
                    )}
                    {terminalTraceControl}
                  </div>
                ) : <div className="w-1/2" />}
                {/* IDO # (the repurposed `freightowner` column) was removed from the form:
                    nobody uses it. Of 1,311,275 orders ever, 172 carried a value and ZERO
                    live orders do; its whole 2026 history is two entries that were typed
                    and cleared the same day, one of them a stray backtick. V1 still shows
                    the field (defs.pl:393), so the column, the read and sr_idonumber all
                    stay — this removes the way to enter it in 360, not the data. */}
                <div className="w-1/2" />
              </Row>
              {renderEtaLfdRow(LABEL_W_MID)}
              {/* Each half follows its own display rule; the row drops only when
                  both are hidden (spacer keeps Out Gate in the right column). */}
              {!(hidden('availableGoodToGoDatetime') && hidden('outGateDatetime')) && (
                <Row>
                  {!hidden('availableGoodToGoDatetime') ? (
                    <div className="w-1/2 flex items-center gap-2 min-w-0">
                      <Label width={LABEL_W_MID}>Available</Label>
                      <ReadValue value={fieldValue('availableGoodToGoDatetime')} mono ariaLabel="Available" />
                    </div>
                  ) : <div className="w-1/2" />}
                  {!hidden('outGateDatetime') && (
                    <div className="w-1/2 flex items-center gap-2 min-w-0">
                      <Label width={LABEL_W_MID}>Out Gate</Label>
                      {/* Read-only mirror of the editable Out Gate Datetime on
                        the Tracing tab. fieldValue surfaces in-flight edits
                        so saving from Tracing immediately reflects here too. */}
                      <ReadValue
                        value={fieldValue('outGateDatetime')}
                        mono
                        width={80}
                        ariaLabel="Out Gate"
                      />
                    </div>
                  )}
                </Row>
              )}
              {!hidden('availToShipDatetime') && (
                <Row>
                  <Label
                    width={LABEL_W_MID}
                    historyFields={['availToShipDatetime']}
                    value={`${availToShipDatetimeVal} ${availToShipUserVal}`.trim()}
                  >
                    Avail To Ship
                  </Label>
                  {availToShipCheckable && (
                    <label className="inline-flex items-center h-6 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={availToShipStamped}
                        onChange={toggleAvailToShip}
                        aria-label="Mark available to ship"
                        className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
                      />
                    </label>
                  )}
                  {changeAllEnabled && availToShipCheckable && (
                    <label
                      className="inline-flex items-center gap-0.5 h-6 cursor-pointer select-none"
                      title="Apply Available to Ship (date + user) — set or cleared — to every pro in this list"
                    >
                      <input
                        type="checkbox"
                        checked={availToShipUpdateAll}
                        onChange={() => setAvailToShipUpdateAll(!availToShipUpdateAll)}
                        aria-label="Apply available to ship to all pros"
                        className="h-3.5 w-3.5 shrink-0 cursor-pointer accent-blue-600"
                      />
                      <span className="text-[0.625rem] font-semibold text-blue-600 dark:text-blue-400">All</span>
                    </label>
                  )}
                  <ReadValue value={availToShipDatetimeVal} mono width={120} ariaLabel="Available to Ship" />
                  <ReadValue value={availToShipUserVal} ariaLabel="Available to Ship User" />
                </Row>
              )}
              <OrderLcFieldGroup isTemplate={isTemplate}>
                {renderTargetDatesRows(LABEL_W_MID)}
              </OrderLcFieldGroup>
            </>
          )}
          <Row>
            <Label width={LABEL_W_MID} bulkFieldName="dropReleaseDatetime" bulkEditable={canEdit('dropReleaseDatetime')} value={s(fieldValue('dropReleaseDatetime'))}>Drop Rel</Label>
            {/* Fills the row like Cust Target above it: at 180px the datetime
                value was clipped to "09/..." once Bobtail Approved shared the
                line, so that flag now sits on its own row below. */}
            <div
              role="group"
              aria-label="Drop Release"
              className={`flex-1 min-w-0 ${conflictClass('dropReleaseDatetime')}`.trim()}
            >
              <DateTimePicker
                mode="datetime"
                inputVariant="compact"
                selectionType="single"
                use24Hour
                usePortal
                openDirection="down"
                autoFlip
                labelType="none"
                placeholder="Date/Time"
                className="w-full [&_div]:!min-w-0"
                disabled={!canEdit('dropReleaseDatetime')}
                value={parseMdYHm(s(fieldValue('dropReleaseDatetime')))}
                onChange={(v) => setField('dropReleaseDatetime', formatMdYHm(v as Date | null))}
              />
            </div>
          </Row>
          {/* Bobtail Approved — `bobtail_approved` char(1) Y/N on ordersall /
                orderscurrent / template (added 2026-09-08). Its own row directly
                under the Drop Release picker (it used to share that row, which
                clipped the date). Same edit gate as its Drop Rel neighbors
                (canEdit); templates open it via templateFieldPolicy.

                A FlagInput rather than a checkbox: the column is a Y/N flag like
                every other order stamp (isTank, hazmat, swing), and a checkbox
                cannot express the 'P' some legacy rows carry — it rendered those
                as unticked, i.e. as 'N'. The select shows them read-only, the
                same way the other flags do. `bulkFieldName` gives it the Change
                All checkbox users asked for; the fan-out itself needed nothing
                new, since bobtailApproved was already in ORDER_FIELD_COLUMNS
                (buildOrderChangeAllRows) and bobtail_approved is already allowed
                on templates (template_change_all_fields.py). */}
          {!hidden('bobtailApproved') && (
            <Row>
              <FlagInput
                label="Bobtail Approved"
                bulkFieldName="bobtailApproved"
                labelWidth={104}
                value={flag(fieldValue('bobtailApproved'))}
                readOnly={!canEdit('bobtailApproved')}
                onChange={(v) => setField('bobtailApproved', v)}
                className={conflictClass('bobtailApproved')}
              />
            </Row>
          )}
          <Row className="!items-start">
            <Label width={LABEL_W_MID} className="mt-1" bulkFieldName="dropReleaseComment" bulkEditable={canEdit('dropReleaseComment')} value={s(fieldValue('dropReleaseComment'))}>Memo</Label>
            <textarea
              value={s(fieldValue('dropReleaseComment'))}
              onChange={(e) => setField('dropReleaseComment', e.target.value)}
              placeholder="Memo"
              rows={3}
              aria-label="Drop Release Memo"
              readOnly={!canEdit('dropReleaseComment')}
              className={
                'flex-1 min-w-0 bg-white dark:bg-gray-900 ' +
                'border border-slate-300 dark:border-gray-600 rounded ' +
                'px-1.5 py-[3px] text-[0.75rem] text-slate-900 ' +
                `dark:text-gray-100 resize-y ${conflictClass('dropReleaseComment')}`
              }
            />
          </Row>
        </Section>

        {/* COLUMN 3 — Locations & Routing */}
        <Section
          title="Locations & Routing"
          icon={<Icon.ship className="w-3.5 h-3.5" />}
          className="pd-col"
        >
          <Row>
            <Label width={LABEL_W_RIGHT} bulkFieldName="pieceCount" bulkEditable={!!editing} value={s(fieldValue('pieceCount'))}>Piece Count</Label>
            {editing ? (
              <TextInput
                value={s(fieldValue('pieceCount'))}
                placeholder={PLACEHOLDER}
                mono
                onChange={(v) => setField('pieceCount', v)}
                ariaLabel="Piece count"
                className={conflictClass('pieceCount')}
              />
            ) : (
              <ReadValue value={d.pieceCount} />
            )}
          </Row>
          <Row>
            <Label width={LABEL_W_RIGHT} bulkFieldName="commodity" bulkEditable={canEditLc('commodity')} value={s(fieldValue('commodity'))}>Commodity</Label>
            {canEditLc('commodity') ? (
              <TextInput
                value={s(fieldValue('commodity'))}
                placeholder={PLACEHOLDER}
                onChange={(v) => setField('commodity', v)}
                ariaLabel="Commodity"
                className={conflictClass('commodity')}
              />
            ) : (
              <ReadValue value={d.commodity} />
            )}
          </Row>
          <Row>
            <Label width={LABEL_W_RIGHT} bulkFieldName="purchaseOrder" bulkEditable={canEditLc('purchaseOrder')} value={s(fieldValue('purchaseOrder'))}>PO</Label>
            {canEditLc('purchaseOrder') ? (
              <TextInput
                value={s(fieldValue('purchaseOrder'))}
                placeholder={PLACEHOLDER}
                mono
                onChange={(v) => setField('purchaseOrder', v)}
                ariaLabel="PO"
                className={conflictClass('purchaseOrder')}
              />
            ) : (
              <ReadValue value={d.purchaseOrder} mono />
            )}
          </Row>
          <Row>
            <Label
              width={LABEL_W_RIGHT}
              historyFields={['lbs', 'kgs', 'metricTons']}
              value={`${s(fieldValue('lbs'))} / ${s(fieldValue('kgs'))} / ${s(fieldValue('metricTons'))}`}
            >
              Lbs / Kgs / MT
            </Label>
            {/* Gated behind a permission container so we can lock weight
                edits down per-group during the overweight-check rollout
                if issues arise. `defaultDisabled` ships the fields in
                view-only mode by default — admins enable edit access in
                the permissions UI when ready. */}
            <PermissionsContainer
              componentId="order-weight-fields"
              componentName="Order Weight Fields"
              defaultDisabled
              showCog
            >
              <WeightFieldsBody
                editable={canEdit('lbs')}
                fieldValue={fieldValue}
                applyWeightCascade={applyWeightCascade}
                normalizeWeightField={normalizeWeightField}
                conflictClass={conflictClass}
                // Highlight uses the ignore-progress variant so already-
                // delivered or transloaded moves still flag the cargo as
                // heavy. Save-blocking + driver re-qual stay on the main
                // result, which respects leg progress (V1 parity).
                overweight={isOverweightForHighlight}
                d={d}
              />
            </PermissionsContainer>
          </Row>
          <Row>
            <Label width={LABEL_W_RIGHT} bulkFieldName="tareLbs" bulkEditable={canEdit('tareLbs')} value={s(fieldValue('tareLbs'))}>Tare Lbs</Label>
            {canEdit('tareLbs') ? (
              <TextInput
                value={s(fieldValue('tareLbs'))}
                placeholder={PLACEHOLDER}
                mono
                width={64}
                onChange={(v) => setField('tareLbs', v === '' ? null : v)}
                ariaLabel="Tare pounds"
                className={conflictClass('tareLbs')}
              />
            ) : (
              <ReadValue value={d.tareLbs} mono />
            )}
          </Row>
          <Row>
            <Label width={LABEL_W_RIGHT} bulkFieldName="sealNumber" bulkEditable={canEdit('sealNumber')} value={s(fieldValue('sealNumber'))}>Seal No.</Label>
            {canEdit('sealNumber') ? (
              <TextInput
                value={s(fieldValue('sealNumber'))}
                placeholder={PLACEHOLDER}
                mono
                onChange={(v) => setField('sealNumber', v)}
                ariaLabel="Seal number"
                className={conflictClass('sealNumber')}
              />
            ) : (
              <ReadValue value={d.sealNumber} mono ariaLabel="Seal number" />
            )}
          </Row>
          {!hidden('vessel') && (
            <Row>
              <Label
                width={LABEL_W_RIGHT}
                historyFields={['vessel', 'voyage']}
                value={hidden('voyage') ? s(fieldValue('vessel')) : `${s(fieldValue('vessel'))} / ${s(fieldValue('voyage'))}`}
              >
                {hidden('voyage') ? 'Ves' : 'Ves / Voy'}
              </Label>
              <BulkFieldCheckbox fieldName="vessel" />
              {canEditLc('vessel') ? (
                <TextInput
                  value={s(fieldValue('vessel'))}
                  onChange={(v) => setField('vessel', v)}
                  ariaLabel="Vessel"
                />
              ) : (
                <ReadValue value={d.vessel} mono ariaLabel="Vessel" />
              )}
              {!hidden('voyage') && (
                <>
                  <BulkFieldCheckbox fieldName="voyage" />
                  {canEditLc('voyage') ? (
                    <TextInput
                      value={s(fieldValue('voyage'))}
                      width={80}
                      onChange={(v) => setField('voyage', v)}
                      ariaLabel="Voyage"
                    />
                  ) : (
                    <ReadValue value={d.voyage} mono width={80} ariaLabel="Voyage" />
                  )}
                </>
              )}
            </Row>
          )}
          <OrderLcFieldGroup isTemplate={isTemplate}>
            {!hidden('portoforiginname') && (
              <Row>
                <Label width={LABEL_W_RIGHT} bulkFieldName="portOfOriginCode" bulkEditable={canEditLc('portOfOrigin')} value={s(fieldValue('portOfOrigin'))}>Port of Orig</Label>
                {canEditLc('portOfOrigin') ? (
                  <PortLookupField
                    code={Number(fieldValue('portOfOriginCode')) || 0}
                    name={s(fieldValue('portOfOrigin'))}
                    onChange={(c, n) => {
                      updateOrderField?.('portOfOriginCode', c);
                      updateOrderField?.('portOfOrigin', n);
                    }}
                    ariaLabel="Port of Origin"
                  />
                ) : (
                  <ReadValue value={d.portOfOrigin} ariaLabel="Port of Origin" />
                )}
              </Row>
            )}
            {!hidden('portofdischargename') && (
              <Row>
                <Label width={LABEL_W_RIGHT} bulkFieldName="portOfDischargeCode" bulkEditable={canEditLc('portOfDischarge')} value={s(fieldValue('portOfDischarge'))}>Port of Disch</Label>
                {canEditLc('portOfDischarge') ? (
                  <PortLookupField
                    code={Number(fieldValue('portOfDischargeCode')) || 0}
                    name={s(fieldValue('portOfDischarge'))}
                    onChange={(c, n) => {
                      updateOrderField?.('portOfDischargeCode', c);
                      updateOrderField?.('portOfDischarge', n);
                    }}
                    ariaLabel="Port of Discharge"
                  />
                ) : (
                  <ReadValue value={d.portOfDischarge} ariaLabel="Port of Discharge" />
                )}
              </Row>
            )}
          </OrderLcFieldGroup>
          {!hidden('pregatepin') && (
            <Row>
              <div className="w-1/2 flex items-center gap-2 min-w-0">
                <Label width={LABEL_W_RIGHT}>PIN</Label>
                <ReadValue value={d.pregatepin} mono ariaLabel="Pre-gate PIN" />
                {/* Why auto-pregate could not get a PIN. Written by the cron and
                  cleared on success, so its presence always explains the blank PIN
                  beside it. Message runs to ~200 chars — hover or click to reveal. */}
                {s(d.pregateerror) !== '' && (
                  <PregateErrorIndicator
                    message={s(d.pregateerror)}
                    label={`Pre-gate error: ${s(d.pregateerror)}`}
                  />
                )}
              </div>
              <div className="w-1/2 flex items-center gap-2 min-w-0">
                <Label width={LABEL_W_RIGHT}>Chass</Label>
                <ReadValue value={d.pregatechassis} mono ariaLabel="Pre-gate chassis" />
                {/* Cancel-pregate icon sits to the right of the chassis field,
                  matching the Empty PIN / Empty Chass row below. */}
                {isPregateRail && s(d.pregatepin) !== '' && (
                  <button
                    type="button"
                    onClick={() => handleCancelPregate()}
                    disabled={cancelingPregate !== null}
                    className="p-0.5 shrink-0 text-brand-red hover:text-red-700 disabled:opacity-40 cursor-pointer transition-colors"
                    title="Cancel pregate appointment"
                    aria-label="Cancel pregate"
                  >
                    <Ban className="w-4 h-4" />
                  </button>
                )}
              </div>
            </Row>
          )}
          {!hidden('emptypregatepin') && (
            <Row>
              <div className="w-1/2 flex items-center gap-2 min-w-0">
                <Label width={LABEL_W_RIGHT}>Empty PIN</Label>
                <ReadValue value={d.emptypregatepin} mono ariaLabel="Empty pre-gate PIN" />
                {/* Populated independently of pregateerror above - which of the two
                  the cron writes depends on the move type. */}
                {s(d.emptypregateerror) !== '' && (
                  <PregateErrorIndicator
                    message={s(d.emptypregateerror)}
                    label={`Empty pre-gate error: ${s(d.emptypregateerror)}`}
                  />
                )}
              </div>
              <div className="w-1/2 flex items-center gap-2 min-w-0">
                <Label width={LABEL_W_RIGHT}>Empty Chass</Label>
                <ReadValue value={d.emptypregatechassis} mono ariaLabel="Empty pre-gate chassis" />
                {/* Cancel-empty-pregate icon sits to the right of the chassis field,
                  consistent with the regular pregate row above. */}
                {isPregateRail && s(d.emptypregatepin) !== '' && (
                  <button
                    type="button"
                    onClick={() => handleCancelPregate('empty')}
                    disabled={cancelingPregate !== null}
                    className="p-0.5 shrink-0 text-brand-red hover:text-red-700 disabled:opacity-40 cursor-pointer transition-colors"
                    title="Cancel empty pregate appointment"
                    aria-label="Cancel empty pregate"
                  >
                    <Ban className="w-4 h-4" />
                  </button>
                )}
              </div>
            </Row>
          )}

          {/* B3: Consignee / Broker / Shipper — read-only display. Consignee
              reads the effective value so a delivery-location → consignee copy
              (applied on the Addl Info tab) reflects here immediately. */}
          <Row>
            <Label width={LABEL_W_RIGHT}>Consignee</Label>
            <ReadValue value={fieldValue('consigneeName')} ariaLabel="Consignee" />
            <EntityEditLink
              kind="location"
              recno={Number(fieldValue('consigneeCode')) || 0}
              label="Consignee"
            />
          </Row>
          <Row>
            <Label width={LABEL_W_RIGHT}>Broker</Label>
            <ReadValue value={d.brokerName} ariaLabel="Broker" />
            <EntityEditLink kind="broker" recno={d.brokerCode} label="Broker" />
          </Row>
          <Row>
            <Label width={LABEL_W_RIGHT}>Broker Contact</Label>
            <ReadValue value={d.brokerContactName} ariaLabel="Broker Contact" />
          </Row>
          <Row>
            <Label width={LABEL_W_RIGHT}>Shipper</Label>
            <ReadValue value={d.shipperName} ariaLabel="Shipper" />
          </Row>

        </Section>

        {/* COLUMN 4 — Comments & Status / Trouble Tickets sub-tabs.
            Templates never carry Trouble Tickets / Service Failures / Repair
            Orders (they're live-ops records), so those sub-tabs are hidden and
            only Comments shows. */}
        <TabbedSection
          title={isTemplate ? 'Comments' : 'Comments & Trouble Tickets'}
          icon={<Icon.comment className="w-3.5 h-3.5" />}
          tabs={
            isTemplate
              ? [{ id: 'cs', label: 'Comments' }]
              : [
                { id: 'cs', label: 'Comments' },
                {
                  id: 'tt',
                  label: 'TTs',
                  badge: effectiveTicketCount > 0 ? effectiveTicketCount : undefined,
                  alert: openTicketCount > 0,
                  alertTitle: openTicketCount === 1
                    ? '1 open (NEW/PENDING) trouble ticket'
                    : `${openTicketCount} open (NEW/PENDING) trouble tickets`,
                },
                {
                  id: 'sf',
                  label: 'SFs',
                  badge: serviceFailureCount > 0 ? serviceFailureCount : undefined,
                },
                {
                  id: 'ro',
                  label: 'ROs',
                  badge: repairOrderCount > 0 ? repairOrderCount : undefined,
                  alert: openRepairOrderCount > 0,
                  alertTitle: openRepairOrderCount === 1
                    ? '1 open repair order on this pro or its equipment'
                    : `${openRepairOrderCount} open repair orders on this pro or its equipment`,
                },
                {
                  id: 'scheduling-requests',
                  label: 'Queue',
                  badge: (openCapacityCount + openCommunicationCount) || undefined,
                },
              ]
          }
          className="pd-col"
          activeId={isTemplate ? 'cs' : commentsTab}
          onTabChange={setCommentsTab}
        >
          {(activeId) =>
            activeId === 'cs' ? (
              <div>
                <Stack>
                  <LabelStacked>Comments</LabelStacked>
                  <BulkFieldCheckbox fieldName="comments" />
                  <textarea
                    value={s(fieldValue('comments'))}
                    placeholder={PLACEHOLDER}
                    rows={8}
                    onChange={(e) => setField('comments', e.target.value)}
                    aria-label="Comments"
                    readOnly={!canEdit('comments')}
                    className={`w-full text-[0.75rem] leading-relaxed rounded border px-2 py-1.5 resize-y text-slate-900 dark:text-gray-100 ${conflicts.comments
                        ? CONFLICT_CLASS
                        : 'border-slate-300 dark:border-gray-600 bg-amber-50 dark:bg-amber-900/30'
                      }`}
                  />
                </Stack>

                <div className="h-1" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2.5">
                  {!hidden('tmwOrderNum') && (
                    <Row>
                      <Label width={LABEL_W_STATUS} bulkFieldName="tmwOrderNum" bulkEditable={canEditLc('tmwOrderNum')}>CST Booking #</Label>
                      {canEditLc('tmwOrderNum') ? (
                        <TextInput
                          value={s(fieldValue('tmwOrderNum'))}
                          onChange={(v) => setField('tmwOrderNum', v)}
                          ariaLabel="CST Booking number"
                        />
                      ) : (
                        <ReadValue value={d.tmwOrderNum} mono ariaLabel="CST Booking number" />
                      )}
                    </Row>
                  )}
                  {!hidden('releasedSet') && (
                    <Row>
                      <Label width={LABEL_W_STATUS} bulkFieldName="releasedSet" bulkEditable={canEditLc('releasedSet')}>Released as Set</Label>
                      {canEditLc('releasedSet') ? (
                        <SelectInput
                          value={s(fieldValue('releasedSet'))}
                          options={['Y', 'N']}
                          width={48}
                          onChange={(v) => setField('releasedSet', v)}
                          ariaLabel="Released as set"
                        />
                      ) : (
                        <ReadValue value={s(d.releasedSet)} width={48} ariaLabel="Released as set" />
                      )}
                    </Row>
                  )}
                  <Row className={s(fieldValue('foodGrade')) === 'Y' ? 'bg-yellow-300 rounded px-1' : ''}>
                    <Label width={LABEL_W_STATUS} bulkFieldName="foodGrade" bulkEditable={canEditLc('foodGrade')}>Food Grade</Label>
                    {canEditLc('foodGrade') ? (
                      <SelectInput
                        value={s(fieldValue('foodGrade'))}
                        options={['Y', 'N']}
                        width={48}
                        onChange={(v) => setField('foodGrade', v)}
                        ariaLabel="Food Grade"
                      />
                    ) : (
                      <ReadValue value={s(d.foodGrade)} width={48} ariaLabel="Food Grade" />
                    )}
                  </Row>
                  <Row>
                    <Label width={LABEL_W_STATUS} bulkFieldName="equipReposition" bulkEditable={canEditLc('equipReposition')}>Equip Reposition</Label>
                    {canEditLc('equipReposition') ? (
                      <SelectInput
                        value={s(fieldValue('equipReposition'))}
                        options={['Y', 'N']}
                        width={48}
                        onChange={(v) => setField('equipReposition', v)}
                        ariaLabel="Equip Reposition"
                      />
                    ) : (
                      <ReadValue value={s(d.equipReposition)} width={48} ariaLabel="Equip Reposition" />
                    )}
                  </Row>
                  <Row>
                    <Label width={LABEL_W_STATUS}>Service Failures</Label>
                    {/* With failures on file the count doubles as a shortcut to
                        the SFs sub-tab; a bare 0 stays plain text. */}
                    {serviceFailureCount > 0 ? (
                      <button
                        type="button"
                        onClick={() => setCommentsTab('sf')}
                        title="View service failures"
                        aria-label={`Service Failures: ${serviceFailureCount} — view service failures`}
                        className="text-[0.75rem] font-semibold bg-brand-red text-white rounded px-1.5 py-0.5 cursor-pointer hover:bg-red-700 transition-colors"
                      >
                        {serviceFailureCount}
                      </button>
                    ) : (
                      <span className="text-[0.75rem] font-semibold text-slate-900 dark:text-gray-100">
                        {serviceFailureCount}
                      </span>
                    )}
                  </Row>
                  <Row>
                    <Label width={LABEL_W_STATUS}>Per Diem</Label>
                    {(() => {
                      // V1: highlight only while the clock is accruing
                      // (enddays - startdays > 0), computed server-side with the
                      // terminate/delivery-leg-arrival end fallback.
                      const pastDue = !!d.perDiemColorActive;
                      return (
                        <span
                          className={
                            pastDue
                              ? 'text-[0.75rem] font-semibold bg-brand-red text-white rounded px-1.5 py-0.5'
                              : 'text-slate-900 dark:text-gray-100 text-[0.75rem] font-semibold'
                          }
                          title={pastDue ? 'Per Diem is past due' : undefined}
                        >
                          {d.perDiem || PLACEHOLDER}
                        </span>
                      );
                    })()}
                  </Row>
                  <Row className="sm:col-span-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Label width={LABEL_W_STATUS} bulkFieldName="offhire" bulkEditable={canEditLc('offhire')}>Off Hire</Label>
                      {canEditLc('offhire') ? (
                        <SelectInput
                          value={s(fieldValue('offhire'))}
                          options={['', 'P', 'R', 'Y', 'N']}
                          width={48}
                          onChange={(v) => setField('offhire', v)}
                          ariaLabel="Off Hire"
                        />
                      ) : (
                        <ReadValue value={s(d.offhire)} width={48} ariaLabel="Off Hire" />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Label width={128} historyFields={['redeliveryExpirationDate']}
                        value={s(fieldValue('redeliveryExpirationDate'))}>Redelivery Expiration</Label>
                      {renderProximityDateValue({
                        fieldKey: 'redeliveryExpirationDate', mode: 'date',
                        editable: canEditLc('redeliveryExpirationDate'),
                        disabled: s(fieldValue('offhire')) !== 'Y',
                        tone: undefined, valueWidth: 120, ariaLabel: 'Redelivery Expiration',
                      })}
                    </div>
                  </Row>
                </div>

                {!isTemplate && (
                  <Row className="mt-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        setGroundedButtonRect(e.currentTarget.getBoundingClientRect());
                        setGroundedOpen((o) => !o);
                      }}
                      className="text-[0.75rem] font-medium text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                      aria-label="Open Grounded Info"
                    >
                      Grounded Info →
                      {groundedInfo.length > 0 && (
                        <span className="ml-0.5 text-[0.625rem] bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full font-semibold">
                          {groundedInfo.length}
                        </span>
                      )}
                    </button>
                  </Row>
                )}
              </div>
            ) : activeId === 'tt' ? (
              <TroubleTicketsPanel
                pronumber={d.pronumber}
                billTo={d.billTo ?? undefined}
                onCountChange={setTicketCount}
                onOpenCountChange={setOpenTicketCount}
                onRescheduleApplied={handleRescheduleApplied}
                onServiceFailureSaved={handleServiceFailureSaved}
                hasUnsavedOrderChanges={editing?.hasChangesIgnoringDispatchFlags ?? false}
                refreshNonce={troubleTicketRefreshNonce}
              />
            ) : activeId === 'scheduling-requests' ? (
              <ProSchedulingRequests communicationFeed={communicationFeed} key={String(d.pronumber)} order={{ pro: String(d.pronumber ?? ''), customer: d.customerName ?? '', market: d.revenueRegion || d.dispatcherRegion || '', move: d.moveType, container: d.container, lfd: capacityDate(d.lastFreeDay), keyTier: d.keyAccount, erd: capacityDate(d.firstRcv), cutoff: capacityDate(d.cutoffDatetime), miles: String(d.legs?.find(l => l.legnum === d.deliveryLeg)?.legMiles ?? ''), appointment: d.legs?.find(l => l.legnum === d.deliveryLeg)?.destApptDate, pool: d.otr === 'Y' ? 'OTR' : 'Local', size: `${d.size || ''} ${d.equipType || ''}`.trim(), pickup: d.legs?.find(l => l.legnum === d.originateLeg)?.origName, delivery: d.legs?.find(l => l.legnum === d.deliveryLeg)?.destName }} />
            ) : activeId === 'sf' ? (
              <ReschedulesPanel
                pronumber={d.pronumber}
                billTo={d.billTo ?? undefined}
                onCountChange={setServiceFailureCount}
                refreshNonce={serviceFailureRefreshNonce}
              />
            ) : (
              <RepairOrdersPanel
                pronumber={d.pronumber}
                onCountChange={setRepairOrderCount}
                onOpenCountChange={setOpenRepairOrderCount}
              />
            )
          }
        </TabbedSection>
      </div>
      {class9NoticeOpen && (
        <InfoModal
          isOpen={true}
          onClose={() => setClass9NoticeOpen(false)}
          title="Class 9 Hazmat"
          maxWidth="sm"
          body={
            <p>
              Class 9 is hazardous on water only. Haz stays N when the only
              hazmat lines on this order are class 9.
            </p>
          }
          buttons={[
            { label: 'OK', onClick: () => setClass9NoticeOpen(false), variant: 'primary' },
          ]}
        />
      )}
      {triaxleConfirm && (
        <InfoModal
          isOpen={true}
          onClose={() => setTriaxleConfirm(null)}
          title="Chassis Type Mismatch"
          body={
            <p>
              The chassis you selected (Triaxle={triaxleConfirm.triaxle}) does not match
              chassis type on order ({fieldValue('chassisType') || '-'}). Are you sure
              you want to select this chassis?
            </p>
          }
          buttons={[
            {
              label: 'OK',
              onClick: () => {
                setField('chassisId', triaxleConfirm.unit);
                setTriaxleConfirm(null);
              },
              variant: 'primary',
            },
            {
              label: 'Cancel',
              onClick: () => setTriaxleConfirm(null),
              variant: 'secondary',
            },
          ]}
        />
      )}
      {d.pronumber != null && !isTemplate && (
        <GroundedDatesPopover
          isOpen={groundedOpen}
          pronumber={Number(d.pronumber)}
          rows={groundedInfo}
          buttonRect={groundedButtonRect}
          onClose={() => setGroundedOpen(false)}
          onChanged={() => { void onProRefresh?.(); }}
        />
      )}
      <WarningConfirmModal
        open={containerValidation.modalOpen}
        title="Container Number Warning"
        message={
          <>
            <span>{containerValidation.warnedValue}</span> doesn't
            pass the standard container check. Please verify it against your
            documentation. Keep the entered value or edit it?
          </>
        }
        confirmLabel="Keep"
        cancelLabel="Edit"
        onConfirm={containerValidation.closeModal}
        onCancel={() => {
          containerValidation.closeModal();
          containerInputRef.current?.focus();
          containerInputRef.current?.select();
        }}
      />
      {overweightToast && createPortal(
        <div
          aria-live="assertive"
          className="pointer-events-none fixed top-4 right-4 z-[9999] w-96 max-w-[calc(100vw-2rem)]"
        >
          <Notification
            type={overweightToast.type}
            title={overweightToast.title}
            message={overweightToast.message}
            showClose
            onClose={dismissOverweightToast}
            autoDismiss={5000}
          />
        </div>,
        document.body,
      )}
      {pregateNotice && createPortal(
        <div
          aria-live="assertive"
          className="pointer-events-none fixed top-4 right-4 z-[9999] w-96 max-w-[calc(100vw-2rem)]"
        >
          <Notification
            type={pregateNotice.type}
            title={pregateNotice.title}
            message={pregateNotice.message}
            showClose
            onClose={() => setPregateNotice(null)}
            autoDismiss={5000}
          />
        </div>,
        document.body,
      )}
      <ScacInfoModal
        isOpen={scacInfoOpen}
        scac={scacValue}
        movetype={d.moveType ?? ''}
        // Templates have a non-numeric "T…" pronumber → Number(...) is NaN, which
        // 422s the (optional) pronumber query param. Omit it instead; SCAC info
        // isn't pro-specific for templates.
        pronumber={
          d.pronumber != null && Number.isFinite(Number(d.pronumber))
            ? Number(d.pronumber)
            : undefined
        }
        region={d.revenueRegion ?? ''}
        onClose={() => setScacInfoOpen(false)}
      />
      <ChassisSpecsModal
        open={chassisSpecsOpen}
        onClose={() => setChassisSpecsOpen(false)}
        details={details}
        editedOrderFields={editedOrderFields}
        updateOrderField={(name, value) => updateOrderField?.(name, value)}
        currentUsername={currentUser}
      />

      {d.pronumber != null && !isTemplate && (
        <StorageClearedPopover
          isOpen={scPopoverOpen}
          pronumber={Number(d.pronumber)}
          buttonRect={scButtonRect}
          onClose={() => setScPopoverOpen(false)}
          onAdded={() => { void onProRefresh?.(); }}
        />
      )}
    </div>
  );

  return d.pronumber != null ? (
    <FieldHistoryProvider
      proNumber={d.pronumber}
      table="ordersall"
      // Unsaved edits included, so Table Maintenance opens on what the user
      // is looking at rather than the last-saved value.
      getFieldValue={(key) => s(fieldValue(key as keyof OrderDetails))}
    >
      {orderInfoBody}
    </FieldHistoryProvider>
  ) : (
    orderInfoBody
  );
};

export default OrderFormView;
